// ═══════════════════════════════════════════════════════════════════════════════
// WritingAnalyzer — ULTRA ENHANCED VERSION  
// Local rubric and diagnostic writing engine using 360-IF architecture
//
// FEATURES:
//   • 20+ rubric criterion types with intelligent scoring
//   • Advanced repetition & plagiarism detection (5-gram, sentence, source)
//   • Reading level analysis (Flesch-Kincaid, SMOG)
//   • Vocabulary sophistication metrics with suggestions
//   • Sentence variety & fluency scoring
//   • Tone & confidence analysis
//   • Argument structure detection (claim, evidence, reasoning, conclusion)
//   • Neural coherence via 223-dimensional embeddings
//   • Comprehensive feedback with strengths, improvements, suggestions
//   • Grade-level appropriate scoring
// ═══════════════════════════════════════════════════════════════════════════════

import { C } from './constants.js';
import { encode, forward, train, isModelTrained, initWeights, cosineSimilarity } from './neuralNetwork.js';
import Pure223Intelligence from './Pure223Intelligence.js';
import EmergenceLayer223 from './EmergenceLayer223.js';
import { splitByParagraphs, autoExtractQA } from './documentProcessor.js';

export const WRITING_PROFILE_SCHEMA_VERSION = 1;
export const WRITING_PROFILE_ANALYZER_VERSION = '360-writing-dual-profile-1.0.0';

const WRITING_PROFILE_DEFINITIONS = Object.freeze({
  expression: Object.freeze({
    areaId: 'written_language_expression',
    areaName: 'Written Language – Expression',
    dimensions: Object.freeze([
      Object.freeze({ id: 'ideas', label: 'Ideas and Development' }),
      Object.freeze({ id: 'organization', label: 'Organization' }),
      Object.freeze({ id: 'evidence', label: 'Evidence' }),
      Object.freeze({ id: 'reasoning', label: 'Reasoning' }),
      Object.freeze({ id: 'narrativeDevelopment', label: 'Narrative Development' })
    ])
  }),
  mechanics: Object.freeze({
    areaId: 'written_language_mechanics',
    areaName: 'Written Language – Mechanics',
    dimensions: Object.freeze([
      Object.freeze({ id: 'grammar', label: 'Grammar' }),
      Object.freeze({ id: 'spelling', label: 'Spelling' }),
      Object.freeze({ id: 'punctuation', label: 'Punctuation' }),
      Object.freeze({ id: 'capitalization', label: 'Capitalization' }),
      Object.freeze({ id: 'sentenceConstruction', label: 'Sentence Construction' })
    ])
  })
});

const boundedScore = value => Math.max(0, Math.min(1, Number.isFinite(Number(value)) ? Number(value) : 0));

const diagnosticDimension = (definition, scoreResult, applicable = true, adjustment = 0) => {
  if (!applicable) {
    return {
      id: definition.id,
      label: definition.label,
      applicable: false,
      percentage: null,
      feedback: 'Not scored for this prompt.',
      source: '360_derived'
    };
  }

  const adjustedScore = boundedScore(boundedScore(scoreResult?.rawScore) - boundedScore(adjustment));
  const baseFeedback = String(scoreResult?.feedback || 'Diagnostic estimate available.');
  return {
    id: definition.id,
    label: definition.label,
    applicable: true,
    percentage: Math.round(adjustedScore * 100),
    feedback: adjustment > 0.05
      ? `Adjusted for response relevance or repetition. ${baseFeedback}`
      : baseFeedback,
    source: '360_derived'
  };
};

const profileFeedback = percentage => {
  if (percentage === null) return 'This area is not part of the current prompt.';
  if (percentage >= 80) return 'The response shows strong skills in this area.';
  if (percentage >= 60) return 'The response shows developing-to-proficient skills in this area.';
  return 'The response indicates that this area may need additional instruction or revision.';
};

const diagnosticProfile = (definition, dimensions) => {
  const applicable = dimensions.filter(dimension => dimension.applicable);
  const percentage = applicable.length
    ? Math.round(applicable.reduce((sum, dimension) => sum + dimension.percentage, 0) / applicable.length)
    : null;
  return {
    areaId: definition.areaId,
    areaName: definition.areaName,
    status: applicable.length ? 'scored' : 'not_applicable',
    percentage,
    calculation: 'mean_of_applicable_dimensions',
    applicableDimensionCount: applicable.length,
    dimensions,
    feedback: profileFeedback(percentage)
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// LINGUISTIC PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

const TRANSITIONS = {
  addition: ['also', 'additionally', 'furthermore', 'moreover', 'in addition', 'besides', 'likewise', 'similarly', 'as well', 'too'],
  contrast: ['however', 'but', 'although', 'yet', 'nevertheless', 'on the other hand', 'while', 'whereas', 'conversely', 'despite', 'in contrast', 'unfortunately', 'instead'],
  cause: ['because', 'since', 'therefore', 'thus', 'consequently', 'as a result', 'hence', 'accordingly', 'due to', 'so'],
  example: ['for example', 'for instance', 'such as', 'specifically', 'to illustrate', 'namely', 'including'],
  sequence: ['first', 'second', 'third', 'next', 'then', 'finally', 'lastly', 'initially', 'subsequently', 'afterward', 'afterwards', 'later', 'before', 'after', 'after that', 'before long', 'soon', 'eventually', 'at last', 'in the end', 'at the same time', 'meanwhile', 'simultaneously', 'during', 'when', 'while', 'as soon as', 'once', 'until', 'by the time'],
  conclusion: ['in conclusion', 'to conclude', 'in summary', 'overall', 'ultimately', 'in short', 'to sum up', 'all in all', 'finally'],
  emphasis: ['indeed', 'certainly', 'in fact', 'above all', 'most importantly', 'especially', 'notably', 'clearly'],
  clarification: ['in other words', 'that is', 'to clarify', 'meaning', 'specifically'],
  time: ['before i knew it', 'suddenly', 'immediately', 'instantly', 'at that moment', 'just then', 'right away', 'without warning']
};

const CLAIM_PATTERNS = {
  strong: [/\b(I strongly believe|I firmly think|It is clear that|Clearly,|Without doubt|Undoubtedly)\b/i],
  moderate: [/\b(I think|I believe|In my opinion|should|must|The author argues|It seems)\b/i],
  weak: [/\b(maybe|perhaps|possibly|might|could be|I guess|sort of|kind of)\b/i]
};

const REASONING_PATTERNS = {
  causal: [/\bbecause\b/i, /\btherefore\b/i, /\bthus\b/i, /\bconsequently\b/i, /\bas a result\b/i],
  evidential: [/\bthis shows\b/i, /\bthis demonstrates\b/i, /\bthis proves\b/i, /\baccording to\b/i],
  logical: [/\bif.*then\b/i, /\bgiven that\b/i, /\bit follows that\b/i],
  comparative: [/\bcompared to\b/i, /\bsimilarly\b/i, /\bin contrast\b/i, /\bunlike\b/i]
};

const EVIDENCE_PATTERNS = {
  textual: [/\b(the text states|the passage says|according to the text|the author writes|as stated)\b/i],
  quotation: [/"[^"]{10,}"/, /"[^"]{10,}"/],
  paraphrase: [/\b(in other words|the author means|this suggests that)\b/i],
  citation: [/\b(paragraph \d|line \d|page \d)\b/i]
};

const SENTENCE_STARTERS = {
  weak: [/^(I think|I believe|I feel|There is|There are|It is|This is)\b/i],
  varied: [/^(Although|While|Despite|Because|Since|When|After|Before|If|Unless)\b/i],
  engaging: [/^(Imagine|Consider|Picture|What if|How would|Why do|Have you ever)\b/i]
};

const COMMON_ERRORS = [
  { wrong: /\bthier\b/gi, right: 'their', type: 'spelling' },
  { wrong: /\bteh\b/gi, right: 'the', type: 'spelling' },
  { wrong: /\brecieve\b/gi, right: 'receive', type: 'spelling' },
  { wrong: /\balot\b/gi, right: 'a lot', type: 'spelling' },
  { wrong: /\bcould of\b/gi, right: 'could have', type: 'grammar' },
  { wrong: /\bshould of\b/gi, right: 'should have', type: 'grammar' },
  { wrong: /\bdefinately\b/gi, right: 'definitely', type: 'spelling' },
  { wrong: /\bseperate\b/gi, right: 'separate', type: 'spelling' }
];

const VOCABULARY = {
  advanced: ['consequently', 'furthermore', 'nevertheless', 'comprehensive', 'significant', 'demonstrate', 'substantial', 'inherent', 'fundamental', 'implications', 'nuanced', 'multifaceted', 'unprecedented'],
  basic: ['good', 'bad', 'big', 'small', 'nice', 'thing', 'stuff', 'very', 'really', 'a lot', 'got', 'said', 'like'],
  overused: ['very', 'really', 'basically', 'actually', 'literally', 'obviously', 'just', 'things', 'stuff']
};

const TONE_INDICATORS = {
  formal: [/\b(therefore|furthermore|consequently|regarding|whereas)\b/gi],
  informal: [/\b(gonna|wanna|kinda|sorta|yeah|nope|stuff|guys)\b/gi],
  persuasive: [/\b(must|should|need to|essential|critical|vital)\b/gi]
};

const HEDGING = ['might', 'may', 'could', 'possibly', 'perhaps', 'seems', 'appears', 'suggests'];
const BOOSTING = ['clearly', 'obviously', 'certainly', 'definitely', 'absolutely', 'always', 'never'];

// ═══════════════════════════════════════════════════════════════════════════════
// NARRATIVE WRITING PATTERNS (Creative/Story Writing)
// ═══════════════════════════════════════════════════════════════════════════════

const NARRATIVE_PATTERNS = {
  // Sensory details by type
  sensory: {
    sight: [/\b(saw|looked|watched|gazed|stared|glanced|peered|noticed|observed|spotted|eye|eyes|bright|dark|shadow|light|glow|shimmer|flash|sparkle|color|red|blue|green|golden|silver|pale|dim|vivid)\b/gi],
    sound: [/\b(heard|listened|sound|noise|whisper|shout|scream|cry|echo|bang|crash|creak|rustle|hum|buzz|roar|silence|quiet|loud|soft|voice|said|asked|replied|murmured|muttered)\b/gi],
    smell: [/\b(smell|scent|odor|fragrance|aroma|stench|whiff|musty|fresh|sweet|sour|rotten|perfume|sniff)\b/gi],
    touch: [/\b(felt|touched|grabbed|held|smooth|rough|cold|hot|warm|cool|soft|hard|sharp|wet|dry|sticky|slippery|texture|skin|hand|hands|finger|fingers)\b/gi],
    taste: [/\b(taste|tasted|flavor|sweet|sour|bitter|salty|delicious|tongue|mouth|eat|ate|drink|drank)\b/gi]
  },
  
  // Similes and metaphors
  figurative: [
    /\blike a[n]?\s+\w+/gi,           // "like a wounded animal"
    /\bas\s+\w+\s+as\b/gi,            // "as cold as ice"
    /\bwas\s+a[n]?\s+\w+/gi,          // "was a shadow" (metaphor)
    /\bseemed\s+like\b/gi,            // "seemed like"
    /\bfelt\s+like\b/gi,              // "felt like"
    /\blooked\s+like\b/gi,            // "looked like"
    /\bsounded\s+like\b/gi,           // "sounded like"
    /\b\w+ing\s+like\b/gi             // "groaning like"
  ],
  
  // Action sequences
  actions: [
    /\b(walked|ran|jumped|climbed|crawled|crept|stepped|moved|rushed|hurried|dashed|sprinted|leaped|fell|stumbled|tripped|reached|grabbed|pulled|pushed|opened|closed|turned|spun|ducked|dodged)\b/gi,
    /\b(entered|exited|left|arrived|approached|retreated|escaped|fled|chased|followed|led|guided)\b/gi,
    /\b(picked up|put down|set down|threw|caught|dropped|lifted|carried|dragged|shoved)\b/gi
  ],
  
  // Character reactions/emotions
  emotions: [
    /\b(felt|feeling|emotion|heart|scared|afraid|frightened|terrified|nervous|anxious|worried|excited|happy|sad|angry|furious|surprised|shocked|confused|curious|brave|courageous)\b/gi,
    /\b(trembled|shook|shivered|froze|gasped|sighed|laughed|cried|smiled|frowned|screamed|shouted|whispered)\b/gi,
    /\b(heart pounded|heart raced|heart beat|pulse quickened|breath caught|held .* breath|took a deep breath|swallowed hard|stomach churned|knees weak)\b/gi
  ],
  
  // Dialogue patterns
  dialogue: [
    /"[^"]+"/g,                        // Quoted speech with regular quotes
    /"[^"]+"/g,                        // Quoted speech with curly quotes
    /'[^']+'/g,                        // Single quoted speech
    /\b(said|asked|replied|answered|whispered|shouted|yelled|screamed|muttered|murmured|exclaimed|demanded|questioned|wondered|called|cried)\b/gi
  ],
  
  // Setting/atmosphere
  setting: [
    /\b(room|house|building|door|window|wall|floor|ceiling|stairs|staircase|hallway|corridor|path|road|street|forest|woods|cave|mountain|river|lake|ocean|beach|sky|sun|moon|stars|night|day|morning|evening|dawn|dusk)\b/gi,
    /\b(old|ancient|abandoned|dark|bright|cold|warm|empty|crowded|quiet|noisy|eerie|creepy|mysterious|strange|familiar|unfamiliar)\b/gi
  ],
  
  // Suspense/tension builders
  suspense: [
    /\b(suddenly|unexpectedly|without warning|all at once|in an instant|before .* knew it)\b/gi,
    /\b(something|someone|somehow|somewhere|nothing|no one|nowhere)\b/gi,
    /\b(strange|mysterious|unknown|hidden|secret|forbidden|dangerous|ominous|haunting|chilling)\b/gi,
    /\b(waited|paused|hesitated|stopped|froze|held .* breath|listened|watched|stared)\b/gi,
    /\?\s*$/gm  // Questions build tension
  ],
  
  // Pacing indicators
  pacing: {
    slow: [/\b(slowly|carefully|cautiously|quietly|gently|softly|gradually)\b/gi],
    fast: [/\b(quickly|suddenly|immediately|instantly|rapidly|swiftly|urgently|desperately)\b/gi],
    short_sentences: /[^.!?]{5,30}[.!?]/g  // Short sentences for tension
  },
  
  // Sequence/time words for narrative flow
  sequence: [
    /\b(first|then|next|after|before|finally|eventually|meanwhile|suddenly|later|soon|now|when|while|as|during)\b/gi
  ]
};

// Descriptive/vivid word lists
const VIVID_VERBS = ['crept', 'lurked', 'echoed', 'shattered', 'pierced', 'engulfed', 'swept', 'plunged', 'soared', 'trembled', 'quivered', 'pounded', 'raced', 'froze', 'melted', 'blazed', 'flickered', 'glowed', 'faded', 'vanished'];
const VIVID_ADJECTIVES = ['eerie', 'ominous', 'desolate', 'ancient', 'crumbling', 'weathered', 'faint', 'distant', 'piercing', 'muffled', 'dusty', 'musty', 'damp', 'frigid', 'sweltering', 'blinding', 'pitch-black', 'moonlit', 'shadowy', 'ghostly'];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN CLASS
// ═══════════════════════════════════════════════════════════════════════════════

export default class WritingAnalyzer {
  constructor() {
    this.trained = false;
    this.trainingMode = 'heuristic';
    this.passageChunks = [];
    this.passageEmbeddings = [];
    this.sourceContent = '';
    this.sourceKeywords = new Set();
    this.sourceSentences = [];
    this.stats = { totalAnalyses: 0, avgScore: 0 };
    
    // Question/prompt analysis
    this.questionText = '';
    this.questionType = 'general';
    this.questionRequirements = [];
    this.questionKeywords = new Set();
    this.questionEntities = [];
    
    // Passage entity extraction (for content-aware scoring)
    this.passageEntities = {
      names: [],           // Character names (Jasmine, Coach Rivera)
      places: [],          // Locations (swimming pool, park, school)
      objects: [],         // Key objects (lighthouse, key, box)
      concepts: [],        // Key concepts/themes
      requiredTerms: []    // Terms that MUST appear in response
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TRAINING
  // ═══════════════════════════════════════════════════════════════════════════

  async trainOnPassage(passageText, opts = {}) {
    // Store and analyze question if provided
    if (opts.questionText) {
      this.analyzeQuestion(opts.questionText);
    }
    
    if (!passageText || passageText.length < 50) {
      this.trained = true;
      this.trainingMode = 'heuristic';
      return;
    }

    this.sourceContent = passageText;
    this.sourceSentences = this._getSentences(passageText);

    // Extract keywords
    const words = passageText.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
    const freq = {};
    words.forEach(w => { freq[w] = (freq[w] || 0) + 1; });
    this.sourceKeywords = new Set(
      Object.entries(freq)
        .filter(([w, c]) => c >= 2 && !this._isStopWord(w))
        .sort((a, b) => b[1] - a[1])
        .slice(0, 50)
        .map(([w]) => w)
    );

    // *** EXTRACT PASSAGE ENTITIES ***
    this._extractPassageEntities(passageText, opts.questionText || '');

    // Chunk passage
    this.passageChunks = splitByParagraphs(passageText, 400);
    if (this.passageChunks.length === 0) {
      this.passageChunks = [{ text: passageText, index: 0 }];
    }

    // Extract Q&A pairs
    const qaPairs = autoExtractQA(passageText);

    // Reset neural components
    try {
      Pure223Intelligence.reset();
      EmergenceLayer223.reset();
    } catch (e) {}

    // Train embeddings
    if (this.passageChunks.length > 0) {
      try {
        const chunkTexts = this.passageChunks.map(c => c.text || c);
        EmergenceLayer223.precomputeChunkEmbeddings(chunkTexts);
        this.passageEmbeddings = EmergenceLayer223.chunkEmbeddings || [];
        Pure223Intelligence.learnFromStructuralQA(
          qaPairs.map(qa => ({ query: qa.q, answer: qa.a, type: qa.type || 'factoid' }))
        );
      } catch (e) {}
    }

    // Train neural network
    if (qaPairs.length >= 2) {
      try {
        initWeights();
        await train({
          pairs: qaPairs,
          epochs: opts.epochs || 60,
          learningRate: 0.015,
          onProgress: (ep, tot) => opts.onProgress?.('training', Math.round((ep / tot) * 100))
        });
        this.trainingMode = 'full';
      } catch (e) {
        this.trainingMode = 'partial';
      }
    } else {
      this.trainingMode = 'heuristic';
    }

    this.trained = true;
    console.log(`[WritingAnalyzer] Training complete. Mode: ${this.trainingMode}, Chunks: ${this.passageChunks.length}, Q&A pairs: ${qaPairs.length}`);
    console.log(`[WritingAnalyzer] Entities extracted - Names: ${this.passageEntities.names.length}, Places: ${this.passageEntities.places.length}, Required: ${this.passageEntities.requiredTerms.length}`);
  }

  // Deterministic preparation used by the authoritative server scorer. It
  // retains the full linguistic, rubric, relevance, entity, and dual-profile
  // analysis while deliberately avoiding mutable/random neural weights. The
  // same response and prompt therefore produce the same saved score on every
  // process, retry, and device.
  prepareDeterministicContext(passageText = '', questionText = '') {
    this.reset();
    if (questionText) this.analyzeQuestion(questionText);
    this.sourceContent = String(passageText || '');
    this.sourceSentences = this._getSentences(this.sourceContent);
    const words = this.sourceContent.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
    const frequencies = {};
    words.forEach(word => { frequencies[word] = (frequencies[word] || 0) + 1; });
    this.sourceKeywords = new Set(
      Object.entries(frequencies)
        .filter(([word, count]) => count >= 2 && !this._isStopWord(word))
        .sort((left, right) => right[1] - left[1])
        .slice(0, 50)
        .map(([word]) => word)
    );
    this._extractPassageEntities(this.sourceContent, questionText);
    this.passageChunks = splitByParagraphs(this.sourceContent, 400);
    if (!this.passageChunks.length && this.sourceContent) {
      this.passageChunks = [{ text: this.sourceContent, index: 0 }];
    }
    this.passageEmbeddings = [];
    this.trainingMode = 'heuristic';
    this.trained = true;
    return this;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PASSAGE ENTITY EXTRACTION
  // ═══════════════════════════════════════════════════════════════════════════

  _extractPassageEntities(passageText, questionText) {
    // Reset entities
    this.passageEntities = {
      names: [],
      places: [],
      objects: [],
      concepts: [],
      requiredTerms: []
    };

    const combined = passageText + ' ' + questionText;
    const passageLower = passageText.toLowerCase();
    const questionLower = questionText.toLowerCase();

    // 1. EXTRACT CHARACTER NAMES
    // Look for capitalized words that appear multiple times or with dialogue tags
    const capitalizedWords = combined.match(/\b[A-Z][a-z]+\b/g) || [];
    const wordCounts = {};
    capitalizedWords.forEach(w => {
      wordCounts[w] = (wordCounts[w] || 0) + 1;
    });

    // Common non-name words to exclude
    const nonNames = new Set([
      'the', 'and', 'but', 'she', 'her', 'his', 'they', 'this', 'that', 'what', 'when', 'where', 'who', 'why', 'how',
      'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
      'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december',
      'today', 'tomorrow', 'yesterday', 'morning', 'afternoon', 'evening', 'night',
      'example', 'instead', 'notice', 'strong', 'weak', 'good', 'best', 'first', 'second', 'third',
      'read', 'write', 'then', 'show', 'describe', 'include', 'use', 'make', 'take',
      'story', 'paragraph', 'sentence', 'writing', 'writers', 'reader', 'readers',
      'every', 'some', 'many', 'most', 'each', 'both', 'either', 'neither',
      'here', 'there', 'now', 'after', 'before', 'during', 'while'
    ]);

    // Character name indicators
    const nameIndicators = [
      /(\w+)\s+(said|asked|replied|whispered|shouted|called|yelled|exclaimed|muttered)/gi,
      /(\w+)'s\s+(eyes|face|hand|heart|voice|mom|dad|mother|father|friend)/gi,
      /(said|asked)\s+(\w+)/gi,
      /(\w+)\s+(was|is|had|felt|looked|saw|knew|wanted|needed|stood|walked|ran)/gi
    ];

    for (const [word, count] of Object.entries(wordCounts)) {
      if (nonNames.has(word.toLowerCase())) continue;
      if (word.length < 3) continue;
      
      // Check if it appears as a name (with dialogue tags or possessives)
      const isLikelyName = nameIndicators.some(pattern => {
        pattern.lastIndex = 0;
        const matches = combined.match(pattern) || [];
        return matches.some(m => m.includes(word));
      });

      // If appears multiple times or has name-like context
      if (count >= 2 || isLikelyName) {
        // Additional check: is followed by action verbs?
        const namePattern = new RegExp(`\\b${word}\\s+(said|asked|looked|walked|ran|felt|knew|wanted|stood|sat|turned|smiled|frowned|nodded|shook)`, 'i');
        const possessivePattern = new RegExp(`\\b${word}'s\\b`, 'i');
        
        if (namePattern.test(combined) || possessivePattern.test(combined) || count >= 3) {
          this.passageEntities.names.push(word);
        }
      }
    }

    // Also check question for specific names mentioned
    const questionNames = questionText.match(/\b[A-Z][a-z]+(?:'s)?\b/g) || [];
    for (const name of questionNames) {
      const cleanName = name.replace(/'s$/, '');
      if (!nonNames.has(cleanName.toLowerCase()) && cleanName.length >= 3) {
        if (!this.passageEntities.names.includes(cleanName)) {
          this.passageEntities.names.push(cleanName);
        }
      }
    }

    // 2. EXTRACT PLACES/LOCATIONS
    const placePatterns = [
      /\b(at the|in the|to the|into the|from the)\s+([a-z]+(?:\s+[a-z]+)?)/gi,
      /\b(school|park|pool|swimming pool|beach|forest|woods|house|home|building|room|classroom|gym|gymnasium|auditorium|library|hospital|store|mall|city|town|village|street|road)\b/gi,
      /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(School|Academy|Park|Pool|Beach|Center|Hospital|Library|Building)\b/g
    ];

    const places = new Set();
    for (const pattern of placePatterns) {
      const matches = combined.match(pattern) || [];
      matches.forEach(m => {
        const place = m.replace(/^(at the|in the|to the|into the|from the)\s+/i, '').trim();
        if (place.length >= 3) {
          places.add(place.toLowerCase());
        }
      });
    }
    this.passageEntities.places = Array.from(places);

    // 3. EXTRACT KEY OBJECTS
    const objectPatterns = [
      /\b(the|a|an)\s+(key|box|letter|book|phone|bag|backpack|door|window|light|flashlight|watch|ring|necklace|photo|picture|map|note|paper)\b/gi
    ];
    const objects = new Set();
    for (const pattern of objectPatterns) {
      const matches = combined.match(pattern) || [];
      matches.forEach(m => {
        const obj = m.replace(/^(the|a|an)\s+/i, '').trim();
        objects.add(obj.toLowerCase());
      });
    }
    this.passageEntities.objects = Array.from(objects);

    // 4. DETERMINE REQUIRED TERMS
    // For narrative continuation tasks, character names from the passage are required
    if (this.questionType === 'narrative' && this.passageEntities.names.length > 0) {
      // Check if question mentions specific character by name
      for (const name of this.passageEntities.names) {
        if (questionLower.includes(name.toLowerCase())) {
          this.passageEntities.requiredTerms.push(name);
        }
      }
      
      // If question says "continue the story" or references "Jasmine's story", etc.
      if (/\b(continue|continuing|write about|story about|for\s+\w+('s)?\s+story)\b/i.test(questionText)) {
        // Add the first/main character as required
        if (this.passageEntities.requiredTerms.length === 0 && this.passageEntities.names.length > 0) {
          this.passageEntities.requiredTerms.push(this.passageEntities.names[0]);
        }
      }
    }

    // For revision tasks, more of the original content is required
    if (this.questionType === 'revision') {
      // Key nouns from the passage become required
      const keyNouns = passageLower.match(/\b(dog|cat|park|friend|school|mom|dad|teacher)\b/g) || [];
      const uniqueNouns = [...new Set(keyNouns)];
      this.passageEntities.requiredTerms.push(...uniqueNouns.slice(0, 5));
    }

    // 5. EXTRACT KEY CONCEPTS FROM QUESTION
    // If question says "write about X", X is a required concept
    const aboutMatch = questionLower.match(/write (?:a paragraph |an? )?(?:about|showing|describing)\s+([^.]+)/i);
    if (aboutMatch) {
      const concept = aboutMatch[1].trim();
      if (concept.length < 50) {
        this.passageEntities.concepts.push(concept);
      }
    }

    console.log(`[WritingAnalyzer] Extracted entities - Names: [${this.passageEntities.names.join(', ')}], Places: [${this.passageEntities.places.join(', ')}], Required: [${this.passageEntities.requiredTerms.join(', ')}]`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // QUESTION/PROMPT ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  analyzeQuestion(questionText) {
    if (!questionText || questionText.length < 10) return;
    
    this.questionText = questionText;
    const lower = questionText.toLowerCase();
    
    // Detect question type
    this.questionType = this._detectQuestionType(lower);
    
    // Extract requirements from question
    this.questionRequirements = this._extractRequirements(lower);
    
    // Extract keywords from question
    const words = lower.match(/\b[a-z]{4,}\b/g) || [];
    this.questionKeywords = new Set(
      words.filter(w => !this._isStopWord(w) && w.length >= 4)
    );
    
    // Extract named entities (capitalized words) from original question
    this.questionEntities = questionText.match(/\b[A-Z][a-z]+\b/g) || [];
    
    console.log(`[WritingAnalyzer] Question analyzed. Type: ${this.questionType}, Requirements: ${this.questionRequirements.length}, Keywords: ${this.questionKeywords.size}`);
  }

  _detectQuestionType(questionLower) {
    // Revision/editing tasks
    if (/\b(revise|fix|correct|edit|rewrite|improve)\b/.test(questionLower) &&
        /\b(error|mistake|spelling|grammar|punctuation|capitalization)\b/.test(questionLower)) {
      return 'revision';
    }
    
    // Narrative/creative writing
    if (/\b(write a (story|paragraph|narrative|scene)|continue the story|concluding paragraph|climax|what happens)\b/.test(questionLower)) {
      return 'narrative';
    }
    
    // Argumentative/persuasive
    if (/\b(argue|argument|persuade|convince|opinion|position|claim|evidence|support)\b/.test(questionLower)) {
      return 'argumentative';
    }
    
    // Expository/informational
    if (/\b(explain|describe|summarize|analyze|compare|contrast|discuss)\b/.test(questionLower)) {
      return 'expository';
    }
    
    // Text-based/evidence
    if (/\b(according to|from the (text|passage)|cite|quote|evidence from)\b/.test(questionLower)) {
      return 'text-based';
    }
    
    return 'general';
  }

  _extractRequirements(questionLower) {
    const requirements = [];
    
    // Content requirements
    const contentPatterns = [
      { pattern: /\b(include|use|add|write|show|describe|explain)\s+(\w+(?:\s+\w+){0,3})/gi, type: 'content' },
      { pattern: /\b(at least|minimum|must have)\s+(\d+|one|two|three|four|five)\s+(\w+)/gi, type: 'quantity' },
      { pattern: /\b(sensory details?|dialogue|description|examples?|evidence|quotes?|reasons?)\b/gi, type: 'element' }
    ];
    
    // Specific instruction patterns
    const instructions = [
      // Narrative elements
      { regex: /\b(sensory detail|sensory language)\b/i, req: 'sensory_details', desc: 'Include sensory details' },
      { regex: /\bdialogu?e\b/i, req: 'dialogue', desc: 'Include dialogue' },
      { regex: /\b(descriptive detail|description)\b/i, req: 'description', desc: 'Include descriptive details' },
      { regex: /\b(how .* feel|emotion|feeling)\b/i, req: 'emotion', desc: 'Show emotions/feelings' },
      { regex: /\b(what .* learn|lesson|realize)\b/i, req: 'lesson', desc: 'Show what is learned' },
      { regex: /\b(character respond|character react|how .* respond)\b/i, req: 'character_response', desc: 'Show character response' },
      { regex: /\bclima?x\b/i, req: 'climax', desc: 'Write the climax' },
      { regex: /\b(conclud|conclusion|ending)\b/i, req: 'conclusion', desc: 'Write a conclusion' },
      { regex: /\b(intense|excitement|tension)\b/i, req: 'intensity', desc: 'Build intensity/tension' },
      { regex: /\bsequence\b/i, req: 'sequence', desc: 'Show sequence of events' },
      
      // Text evidence
      { regex: /\b(cite|quote|evidence from|according to)\b/i, req: 'text_evidence', desc: 'Cite text evidence' },
      { regex: /\b(from the (text|passage|story))\b/i, req: 'passage_reference', desc: 'Reference the passage' },
      
      // Argumentative
      { regex: /\b(claim|thesis|argument|position)\b/i, req: 'claim', desc: 'State a claim/thesis' },
      { regex: /\b(support|reason|because|why)\b/i, req: 'support', desc: 'Provide support/reasons' },
      { regex: /\b(example|instance|evidence)\b/i, req: 'examples', desc: 'Include examples' },
      
      // Revision
      { regex: /\b(fix|correct).*(error|mistake)\b/i, req: 'fix_errors', desc: 'Fix errors' },
      { regex: /\bcapitalization\b/i, req: 'fix_caps', desc: 'Fix capitalization' },
      { regex: /\bpunctuation\b/i, req: 'fix_punct', desc: 'Fix punctuation' },
      { regex: /\bspelling\b/i, req: 'fix_spelling', desc: 'Fix spelling' },
      { regex: /\bgrammar\b/i, req: 'fix_grammar', desc: 'Fix grammar' },
      { regex: /\b(sentence structure|complete sentence)\b/i, req: 'fix_structure', desc: 'Fix sentence structure' },
      { regex: /\b(same (basic )?story|keep the|maintain)\b/i, req: 'maintain_content', desc: 'Keep the same content' },
      
      // Quantity
      { regex: /\bat least (\d+|one|two|three)/i, req: 'minimum_count', desc: 'Meet minimum count' },
      { regex: /\b(three|3) different/i, req: 'three_items', desc: 'Include at least 3 items' }
    ];
    
    for (const { regex, req, desc } of instructions) {
      if (regex.test(questionLower)) {
        requirements.push({ type: req, description: desc, pattern: regex });
      }
    }
    
    return requirements;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  analyzeWriting(studentText, rubricCriteria, questionText = null) {
    // Analyze question if provided (and not already analyzed)
    if (questionText && questionText !== this.questionText) {
      this.analyzeQuestion(questionText);
    }
    
    if (!studentText || studentText.trim().length < 10) {
      return this._emptyResult(rubricCriteria);
    }

    this.stats.totalAnalyses++;

    try {
      // Comprehensive text analysis
      const analysis = this._comprehensiveAnalysis(studentText);

      // Mechanics checks are meaningful only for language-like writing. A
      // keyboard-mashed response can otherwise appear to have few grammar or
      // spelling errors and receive an inflated diagnostic profile.
      const responseValidity = this._assessResponseValidity(studentText, analysis);
      if (!responseValidity.valid) {
        return this._invalidResponseResult(studentText, rubricCriteria, analysis, responseValidity);
      }

      // Get neural embedding
      let embed = null;
      if (this.trainingMode !== 'heuristic' && isModelTrained()) {
        try {
          embed = Array.from(forward(encode(studentText)).h2);
        } catch (e) {}
      }

      // Calculate evidence score
      const evidenceScore = this._calcEvidence(studentText, embed);

      // Check for off-topic/irrelevant response
      const relevancePenalty = this._detectOffTopic(studentText, embed);
      
      // *** CHECK ENTITY MATCHING ***
      const entityResults = this._checkEntityMatching(studentText);
      
      // Check question requirements
      const requirementResults = this._checkRequirements(studentText, analysis);
      const requirementPenalty = requirementResults.penalty;

      // Score each criterion
      const scores = {};
      for (const c of rubricCriteria) {
        const s = this._scoreCriterion(studentText, embed, c, analysis);
        
        // Apply repetition penalty (except conventions)
        const name = (c.name || '').toLowerCase();
        if (analysis.repetitionPenalty > 0 && !['conventions', 'grammar', 'capitalization', 'error correction'].includes(name)) {
          s.rawScore = Math.max(0, s.rawScore - analysis.repetitionPenalty);
          s.points = Math.round(s.rawScore * c.weight * 10) / 10;
        }
        
        // Apply off-topic penalty (except conventions-related criteria)
        if (relevancePenalty > 0 && !['conventions', 'grammar', 'capitalization', 'spelling', 'punctuation'].includes(name)) {
          s.rawScore = Math.max(0, s.rawScore - relevancePenalty);
          s.points = Math.round(s.rawScore * c.weight * 10) / 10;
          if (relevancePenalty > 0.3) {
            s.feedback = '⚠️ Response may be off-topic. ' + s.feedback;
          }
        }
        
        // Apply entity mismatch penalty (wrong characters/places)
        if (entityResults.penalty > 0 && !['conventions', 'grammar', 'capitalization', 'spelling', 'punctuation'].includes(name)) {
          s.rawScore = Math.max(0, s.rawScore - entityResults.penalty);
          s.points = Math.round(s.rawScore * c.weight * 10) / 10;
          if (entityResults.penalty > 0.3 && entityResults.missingRequired.length > 0) {
            s.feedback = `⚠️ Missing required elements: ${entityResults.missingRequired.join(', ')}. ` + s.feedback;
          }
        }
        
        // Apply requirement penalty for revision tasks (must maintain content)
        if (this.questionType === 'revision' && requirementPenalty > 0.3 && 
            !['conventions', 'grammar', 'capitalization', 'spelling', 'punctuation'].includes(name)) {
          s.rawScore = Math.max(0, s.rawScore - requirementPenalty);
          s.points = Math.round(s.rawScore * c.weight * 10) / 10;
          s.feedback = '⚠️ Response does not match the original text to revise. ' + s.feedback;
        }
        
        scores[c.name] = s;
      }

      // Calculate totals
      const { total, maxPossible } = this._calcWeighted(scores, rubricCriteria);
      const pct = maxPossible > 0 ? Math.round((total / maxPossible) * 100) : 0;

      // The legacy holistic rubric remains the pass/fail authority. These two
      // profiles are diagnostic views of the same response and never alter it.
      const writingProfiles = this._buildWritingProfiles({
        text: studentText,
        embed,
        analysis,
        rubricCriteria,
        holisticPercentage: pct,
        repetitionPenalty: analysis.repetitionPenalty,
        relevancePenalty,
        entityPenalty: entityResults.penalty
      });
      
      // Generate feedback
      const fb = this._genFeedback(scores, rubricCriteria, analysis, pct);
      
      // Build overall feedback with warnings
      let overallFeedback = fb.overall;
      
      // Add entity mismatch warning
      if (entityResults.penalty > 0.3 && entityResults.missingRequired.length > 0) {
        overallFeedback = `⚠️ Your response should include: ${entityResults.missingRequired.join(', ')}. ` + overallFeedback;
      }
      
      // Add requirement warnings
      if (requirementResults.missingRequirements.length > 0 && requirementPenalty > 0.2) {
        const missingList = requirementResults.missingRequirements.slice(0, 3).map(r => r.description).join(', ');
        overallFeedback = `⚠️ Missing requirements: ${missingList}. ` + overallFeedback;
      }
      
      // Add off-topic warning
      if (relevancePenalty > 0.3) {
        overallFeedback = '⚠️ Your response may be off-topic. Make sure to address the passage/prompt directly. ' + overallFeedback;
      }
      
      // Add question type specific feedback
      if (this.questionType === 'revision' && requirementPenalty > 0.3) {
        overallFeedback = '⚠️ This appears to be a different text, not a revision of the original. Please revise the provided draft. ' + overallFeedback;
      }

      return {
        scores,
        total,
        maxPossible,
        percentage: pct,
        evidenceScore,
        overallFeedback: overallFeedback,
        strengths: fb.strengths,
        areasForImprovement: fb.improvements,
        suggestions: fb.suggestions,
        wordCount: analysis.wordCount,
        sentenceCount: analysis.sentenceCount,
        readingLevel: analysis.readingLevel,
        vocabularyLevel: analysis.vocabulary,
        sentenceVariety: analysis.sentenceVariety,
        toneAnalysis: analysis.tone,
        argumentStructure: analysis.argument,
        repetitionPenalty: analysis.repetitionPenalty,
        relevancePenalty: relevancePenalty,
        requirementsPenalty: requirementPenalty,
        requirementsMet: requirementResults.metRequirements,
        requirementsMissing: requirementResults.missingRequirements,
        entityPenalty: entityResults.penalty,
        entitiesFound: entityResults.found,
        entitiesMissing: entityResults.missingRequired,
        passageEntities: this.passageEntities,
        questionType: this.questionType,
        originalityScore: Math.round((1 - analysis.repetitionPenalty) * 100) / 100,
        coherenceScore: analysis.coherence,
        analysisMode: this.trainingMode,
        responseValidity,
        writingProfiles
      };
    } catch (e) {
      console.error('[WritingAnalyzer] Error:', e);
      return this._heuristicScore(studentText, rubricCriteria);
    }
  }

  _buildWritingProfiles({
    text,
    embed,
    analysis,
    rubricCriteria = [],
    holisticPercentage = 0,
    repetitionPenalty = 0,
    relevancePenalty = 0,
    entityPenalty = 0,
    responseValidity = { valid: true }
  }) {
    const hasScorableResponse = String(text || '').trim().length >= 10;
    const scoreOrEmpty = scorer => hasScorableResponse && responseValidity.valid !== false
      ? scorer()
      : {
          rawScore: 0,
          feedback: responseValidity.valid === false
            ? 'Not scored because the response does not contain enough recognizable language.'
            : 'No response provided.',
          details: {}
        };
    const rubricNames = rubricCriteria
      .map(criterion => String(criterion?.name || '').trim().toLowerCase())
      .filter(Boolean);
    const hasRubricSignal = pattern => rubricNames.some(name => pattern.test(name));
    const prompt = `${this.questionText || ''}\n${this.sourceContent || ''}`.toLowerCase();
    const isRevision = this.questionType === 'revision';

    const expressionRubric = hasRubricSignal(/\b(ideas?|development|elaboration|organization|coherence|evidence|reasoning|narrative|plot|claim|support)\b/);
    const expressionApplicable = !isRevision || expressionRubric;
    const evidenceApplicable = expressionApplicable && (
      Boolean(this.sourceContent)
      || ['argumentative', 'text-based'].includes(this.questionType)
      || /\b(evidence|cite|quote|according to|support(?:ing)? detail|source|passage|text)\b/.test(prompt)
      || hasRubricSignal(/\b(evidence|support|example|citation|quote)\b/)
    );
    const reasoningApplicable = expressionApplicable && (
      ['argumentative', 'expository', 'text-based'].includes(this.questionType)
      || /\b(reason|reasoning|explain|why|because|analy[sz]e|argument|claim)\b/.test(prompt)
      || hasRubricSignal(/\b(reasoning|logic|analysis|claim|thesis)\b/)
    );
    const narrativeApplicable = expressionApplicable && (
      this.questionType === 'narrative'
      || /\b(story|narrative|plot|character|climax|resolution|sequence of events)\b/.test(prompt)
      || hasRubricSignal(/\b(narrative|story|plot|character|climax|resolution)\b/)
    );

    // Relevance, entity mismatch, and only high-confidence repetition affect
    // expression. Small lexical-diversity warnings are not strong enough to
    // lower a diagnostic profile, and none of these content signals may lower
    // mechanics.
    const highConfidenceRepetitionPenalty = Number(repetitionPenalty) >= 0.35
      ? boundedScore(repetitionPenalty)
      : 0;
    const expressionAdjustment = Math.min(
      0.85,
      highConfidenceRepetitionPenalty + boundedScore(relevancePenalty) + boundedScore(entityPenalty)
    );
    const expressionDefinitions = WRITING_PROFILE_DEFINITIONS.expression.dimensions;
    const expressionDimensions = [
      diagnosticDimension(expressionDefinitions[0], scoreOrEmpty(() => this._scoreDevelopment(text, analysis)), expressionApplicable, expressionAdjustment),
      diagnosticDimension(expressionDefinitions[1], scoreOrEmpty(() => this._scoreCoherence(text, analysis)), expressionApplicable, expressionAdjustment),
      diagnosticDimension(expressionDefinitions[2], scoreOrEmpty(() => this._scoreEvidence(text, embed)), evidenceApplicable, expressionAdjustment),
      diagnosticDimension(expressionDefinitions[3], scoreOrEmpty(() => this._scoreReasoning(text, analysis)), reasoningApplicable, expressionAdjustment),
      diagnosticDimension(expressionDefinitions[4], scoreOrEmpty(() => this._scoreNarrativeDevelopment(text, analysis)), narrativeApplicable, expressionAdjustment)
    ];

    const mechanicsDefinitions = WRITING_PROFILE_DEFINITIONS.mechanics.dimensions;
    const mechanicsDimensions = [
      diagnosticDimension(mechanicsDefinitions[0], scoreOrEmpty(() => this._scoreGrammar(text))),
      diagnosticDimension(mechanicsDefinitions[1], scoreOrEmpty(() => this._scoreSpelling(text))),
      diagnosticDimension(mechanicsDefinitions[2], scoreOrEmpty(() => this._scorePunctuation(text))),
      diagnosticDimension(mechanicsDefinitions[3], scoreOrEmpty(() => this._scoreCapitalization(text))),
      diagnosticDimension(mechanicsDefinitions[4], scoreOrEmpty(() => this._scoreSentenceStructure(text)))
    ];

    return {
      schemaVersion: WRITING_PROFILE_SCHEMA_VERSION,
      analyzerVersion: WRITING_PROFILE_ANALYZER_VERSION,
      profileType: 'response_diagnostic',
      diagnosticOnly: true,
      affectsOverallScore: false,
      holisticPercentage,
      analysisMode: this.trainingMode,
      expression: diagnosticProfile(WRITING_PROFILE_DEFINITIONS.expression, expressionDimensions),
      mechanics: diagnosticProfile(WRITING_PROFILE_DEFINITIONS.mechanics, mechanicsDimensions)
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ENTITY MATCHING CHECK
  // ═══════════════════════════════════════════════════════════════════════════

  _checkEntityMatching(studentText) {
    const results = {
      found: {
        names: [],
        places: [],
        objects: []
      },
      missingRequired: [],
      penalty: 0
    };

    const studentLower = studentText.toLowerCase();

    // Check which passage names appear in student text
    for (const name of this.passageEntities.names) {
      if (studentText.includes(name) || studentLower.includes(name.toLowerCase())) {
        results.found.names.push(name);
      }
    }

    // Check which passage places appear in student text
    for (const place of this.passageEntities.places) {
      if (studentLower.includes(place.toLowerCase())) {
        results.found.places.push(place);
      }
    }

    // Check which passage objects appear in student text
    for (const obj of this.passageEntities.objects) {
      if (studentLower.includes(obj.toLowerCase())) {
        results.found.objects.push(obj);
      }
    }

    // Check REQUIRED terms
    for (const required of this.passageEntities.requiredTerms) {
      const found = studentText.includes(required) || studentLower.includes(required.toLowerCase());
      if (!found) {
        results.missingRequired.push(required);
      }
    }

    // Calculate penalty based on missing required terms
    if (this.passageEntities.requiredTerms.length > 0) {
      const missingRatio = results.missingRequired.length / this.passageEntities.requiredTerms.length;
      if (missingRatio >= 1) {
        // All required terms missing = probably wrong topic entirely
        results.penalty = 0.5;
      } else if (missingRatio >= 0.5) {
        results.penalty = 0.3;
      } else if (missingRatio > 0) {
        results.penalty = 0.15;
      }
    }

    // Additional penalty if passage has character names but student uses DIFFERENT names
    if (this.passageEntities.names.length > 0 && results.found.names.length === 0) {
      // Check if student invented their own character names
      const studentNames = studentText.match(/\b[A-Z][a-z]{2,}\b/g) || [];
      const nonPassageNames = studentNames.filter(n => 
        !this.passageEntities.names.includes(n) && 
        !['The', 'And', 'But', 'She', 'Her', 'His', 'They', 'This', 'That', 'Then', 'When', 'What', 'Where', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(n)
      );

      if (nonPassageNames.length >= 2 && this.passageEntities.requiredTerms.length > 0) {
        // Student wrote about completely different characters
        results.penalty = Math.max(results.penalty, 0.4);
        if (!results.missingRequired.includes(this.passageEntities.names[0])) {
          results.missingRequired.push(this.passageEntities.names[0]);
        }
        console.log(`[WritingAnalyzer] Different characters detected. Passage: [${this.passageEntities.names.join(', ')}], Student: [${nonPassageNames.join(', ')}]`);
      }
    }

    console.log(`[WritingAnalyzer] Entity check - Found names: [${results.found.names.join(', ')}], Missing required: [${results.missingRequired.join(', ')}], Penalty: ${results.penalty}`);

    return results;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPREHENSIVE ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  _comprehensiveAnalysis(text) {
    const sentences = this._getSentences(text);
    const words = text.match(/\b[a-zA-Z]+\b/g) || [];
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 20);

    return {
      wordCount: words.length,
      sentenceCount: sentences.length,
      paragraphCount: Math.max(1, paragraphs.length),
      avgSentLen: sentences.length > 0 ? Math.round(words.length / sentences.length * 10) / 10 : 0,
      readingLevel: this._readingLevel(text, sentences, words),
      vocabulary: this._vocabAnalysis(text, words),
      sentenceVariety: this._sentenceVariety(sentences),
      tone: this._toneAnalysis(text),
      argument: this._argumentAnalysis(text),
      repetitionPenalty: this._detectRepetition(text),
      coherence: this._coherence(sentences),
      sentences,
      words
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // READING LEVEL
  // ═══════════════════════════════════════════════════════════════════════════

  _readingLevel(text, sentences, words) {
    if (words.length < 10) return { gradeLevel: 0, difficulty: 'insufficient' };
    
    const syllables = words.reduce((s, w) => s + this._syllables(w), 0);
    const avgSentLen = words.length / sentences.length;
    const avgSyllPerWord = syllables / words.length;
    
    const fkGrade = 0.39 * avgSentLen + 11.8 * avgSyllPerWord - 15.59;
    const grade = Math.max(1, Math.min(16, Math.round(fkGrade)));
    
    let difficulty = 'intermediate';
    if (grade <= 5) difficulty = 'elementary';
    else if (grade <= 8) difficulty = 'middle school';
    else if (grade <= 12) difficulty = 'high school';
    else difficulty = 'college';
    
    return {
      gradeLevel: grade,
      fleschKincaid: Math.round(fkGrade * 10) / 10,
      difficulty
    };
  }

  _syllables(word) {
    word = word.toLowerCase().replace(/[^a-z]/g, '');
    if (word.length <= 3) return 1;
    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/, '');
    return (word.match(/[aeiouy]{1,2}/g) || [1]).length;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VOCABULARY ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  _vocabAnalysis(text, words) {
    const lower = text.toLowerCase();
    const unique = new Set(words.map(w => w.toLowerCase()));
    const ttr = unique.size / Math.max(words.length, 1);

    let advanced = [], basic = [], overused = [];
    for (const w of VOCABULARY.advanced) {
      if (lower.includes(w)) advanced.push(w);
    }
    for (const w of VOCABULARY.basic) {
      if ((lower.match(new RegExp(`\\b${w}\\b`, 'gi')) || []).length > 0) basic.push(w);
    }
    for (const w of VOCABULARY.overused) {
      if ((lower.match(new RegExp(`\\b${w}\\b`, 'gi')) || []).length > 2) overused.push(w);
    }

    let score = 0.5 + advanced.length * 0.08 - basic.length * 0.02 - overused.length * 0.03 + (ttr - 0.5) * 0.3;
    score = Math.max(0, Math.min(1, score));

    const level = score >= 0.75 ? 'sophisticated' : score >= 0.55 ? 'proficient' : score >= 0.4 ? 'adequate' : 'developing';

    // Generate suggestions
    const suggestions = [];
    const repl = {
      'good': 'excellent, beneficial',
      'bad': 'detrimental, harmful',
      'big': 'significant, substantial',
      'very': 'extremely, remarkably',
      'things': 'factors, elements'
    };
    for (const w of [...new Set([...basic, ...overused])].slice(0, 2)) {
      if (repl[w]) suggestions.push(`Replace "${w}" with ${repl[w]}`);
    }

    return {
      level,
      score: Math.round(score * 100) / 100,
      ttr: Math.round(ttr * 100) / 100,
      advancedWords: advanced,
      suggestions
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SENTENCE VARIETY
  // ═══════════════════════════════════════════════════════════════════════════

  _sentenceVariety(sentences) {
    if (sentences.length < 2) return { score: 0.5, variety: 'insufficient' };

    const lengths = sentences.map(s => s.split(/\s+/).length);
    const avg = lengths.reduce((a, b) => a + b, 0) / lengths.length;
    const stdDev = Math.sqrt(lengths.reduce((s, l) => s + Math.pow(l - avg, 2), 0) / lengths.length);
    const lenScore = Math.min(1, stdDev / 8);

    const starts = { weak: 0, varied: 0, engaging: 0 };
    for (const s of sentences) {
      for (const [t, ps] of Object.entries(SENTENCE_STARTERS)) {
        if (ps.some(p => p.test(s))) { starts[t]++; break; }
      }
    }

    const begScore = Math.min(1, (1 - starts.weak / sentences.length) * 0.5 + (starts.varied + starts.engaging) / sentences.length * 0.5 + 0.3);
    const score = Math.min(1, lenScore * 0.4 + begScore * 0.6);
    
    const variety = score >= 0.7 ? 'excellent' : score >= 0.5 ? 'good' : score >= 0.35 ? 'adequate' : 'monotonous';

    return {
      score: Math.round(score * 100) / 100,
      variety,
      avgLength: Math.round(avg)
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TONE ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  _toneAnalysis(text) {
    const scores = {};
    for (const [t, ps] of Object.entries(TONE_INDICATORS)) {
      let c = 0;
      for (const p of ps) c += (text.match(p) || []).length;
      scores[t] = c;
    }

    let hedge = 0, boost = 0;
    const lower = text.toLowerCase();
    for (const w of HEDGING) if (lower.includes(w)) hedge++;
    for (const w of BOOSTING) if (lower.includes(w)) boost++;

    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const dominant = sorted[0][1] > 0 ? sorted[0][0] : 'neutral';
    const confidence = boost > hedge * 2 ? 'assertive' : hedge > boost * 2 ? 'tentative' : 'moderate';
    const formality = scores.formal > scores.informal ? 'formal' : scores.informal > scores.formal ? 'informal' : 'neutral';

    return { dominant, confidence, formality };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ARGUMENT ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  _argumentAnalysis(text) {
    const struct = {
      hasClaim: false,
      hasEvidence: false,
      hasReasoning: false,
      hasConclusion: false,
      components: []
    };

    for (const ps of Object.values(CLAIM_PATTERNS)) {
      if (ps.some(p => p.test(text))) {
        struct.hasClaim = true;
        struct.components.push('claim');
        break;
      }
    }

    for (const ps of Object.values(EVIDENCE_PATTERNS)) {
      if (ps.some(p => p.test(text))) {
        struct.hasEvidence = true;
        struct.components.push('evidence');
        break;
      }
    }

    for (const ps of Object.values(REASONING_PATTERNS)) {
      if (ps.some(p => p.test(text))) {
        struct.hasReasoning = true;
        struct.components.push('reasoning');
        break;
      }
    }

    if (TRANSITIONS.conclusion.some(w => text.toLowerCase().includes(w))) {
      struct.hasConclusion = true;
      struct.components.push('conclusion');
    }

    struct.completeness = Math.round((struct.components.length / 4) * 100);
    struct.type = struct.hasClaim && struct.hasEvidence && struct.hasReasoning ? 'well-developed' :
                  struct.hasClaim && (struct.hasEvidence || struct.hasReasoning) ? 'developing' :
                  struct.hasClaim ? 'basic' : 'undeveloped';

    return struct;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REPETITION DETECTION
  // ═══════════════════════════════════════════════════════════════════════════

  _detectRepetition(text) {
    let penalty = 0;
    const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 0);

    // 1. Repeated 5-word phrases
    const phraseCounts = {};
    for (let i = 0; i <= words.length - 5; i++) {
      const p = words.slice(i, i + 5).join(' ');
      phraseCounts[p] = (phraseCounts[p] || 0) + 1;
    }
    const repeated = Object.values(phraseCounts).filter(c => c > 1);
    if (repeated.length > 0) {
      penalty += Math.min(0.4, (Math.max(...repeated) - 1) * 0.12);
      console.log('[WritingAnalyzer] Repeated phrases detected, max repeats:', Math.max(...repeated));
    }

    // 2. Low unique word ratio
    const unique = new Set(words);
    const ratio = unique.size / Math.max(words.length, 1);
    if (ratio < 0.35 && words.length > 30) {
      penalty += 0.35;
      console.log('[WritingAnalyzer] Very low unique word ratio:', ratio);
    } else if (ratio < 0.45 && words.length > 30) {
      penalty += 0.2;
    } else if (ratio < 0.55 && words.length > 30) {
      penalty += 0.1;
    }

    // 3. Repeated sentences
    const sents = text.split(/[.!?]+/).map(s => s.trim().toLowerCase()).filter(s => s.length > 15);
    if (sents.length >= 3 && new Set(sents).size < sents.length * 0.7) {
      penalty += 0.25;
      console.log('[WritingAnalyzer] Repeated sentences detected');
    }

    // 4. Copying from source passage
    if (this.sourceSentences.length > 0) {
      const studentSents = this._getSentences(text);
      let copied = 0;
      for (const ss of studentSents) {
        const ssW = new Set(ss.toLowerCase().split(/\s+/));
        for (const src of this.sourceSentences) {
          const srcW = new Set(src.toLowerCase().split(/\s+/));
          let overlap = 0;
          for (const w of ssW) if (srcW.has(w)) overlap++;
          if (overlap > Math.min(ssW.size, srcW.size) * 0.8) {
            copied++;
            break;
          }
        }
      }
      if (copied > studentSents.length * 0.5) {
        penalty += 0.3;
        console.log('[WritingAnalyzer] Heavy copying from source detected');
      } else if (copied > studentSents.length * 0.3) {
        penalty += 0.15;
      }
    }

    // 5. GIBBERISH / NONSENSE DETECTION
    const gibberishPenalty = this._detectGibberish(text, words);
    if (gibberishPenalty > 0) {
      penalty += gibberishPenalty;
      console.log('[WritingAnalyzer] Gibberish detected, penalty:', gibberishPenalty);
    }

    return Math.min(0.85, penalty); // Increased cap to 85% for severe cases
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GIBBERISH / NONSENSE DETECTION
  // ═══════════════════════════════════════════════════════════════════════════

  _detectGibberish(text, words) {
    let penalty = 0;
    
    // Common English words for reference
    const commonWords = new Set([
      'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
      'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
      'should', 'may', 'might', 'must', 'shall', 'can', 'need', 'to', 'of',
      'in', 'for', 'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through',
      'and', 'but', 'or', 'nor', 'so', 'yet', 'both', 'either', 'neither',
      'not', 'no', 'yes', 'this', 'that', 'these', 'those', 'it', 'its',
      'they', 'them', 'their', 'he', 'him', 'his', 'she', 'her', 'we', 'us',
      'our', 'you', 'your', 'i', 'me', 'my', 'who', 'what', 'where', 'when',
      'why', 'how', 'which', 'there', 'here', 'all', 'each', 'every', 'some',
      'any', 'most', 'other', 'another', 'such', 'only', 'own', 'same', 'than',
      'too', 'very', 'just', 'also', 'now', 'then', 'more', 'much', 'many',
      'about', 'after', 'before', 'between', 'under', 'over', 'again', 'still',
      'said', 'like', 'go', 'went', 'come', 'came', 'see', 'saw', 'look',
      'looked', 'know', 'knew', 'think', 'thought', 'make', 'made', 'get',
      'got', 'take', 'took', 'want', 'wanted', 'use', 'used', 'find', 'found',
      'give', 'gave', 'tell', 'told', 'feel', 'felt', 'try', 'tried', 'leave',
      'left', 'call', 'called', 'keep', 'kept', 'let', 'begin', 'began', 'seem',
      'help', 'show', 'hear', 'heard', 'play', 'run', 'move', 'live', 'believe',
      'hold', 'bring', 'happen', 'write', 'wrote', 'sit', 'sat', 'stand', 'stood',
      'lose', 'lost', 'pay', 'paid', 'meet', 'met', 'include', 'continue', 'set',
      'learn', 'change', 'lead', 'understand', 'watch', 'follow', 'stop', 'create',
      'speak', 'read', 'spend', 'grow', 'open', 'walk', 'win', 'teach', 'offer',
      'remember', 'love', 'consider', 'appear', 'buy', 'wait', 'serve', 'die',
      'send', 'build', 'stay', 'fall', 'cut', 'reach', 'kill', 'remain', 'suggest',
      'raise', 'pass', 'sell', 'require', 'report', 'decide', 'pull', 'morning',
      'afternoon', 'evening', 'night', 'day', 'week', 'month', 'year', 'time',
      'way', 'thing', 'man', 'woman', 'child', 'world', 'life', 'hand', 'part',
      'place', 'case', 'week', 'company', 'system', 'program', 'question', 'work',
      'government', 'number', 'night', 'point', 'home', 'water', 'room', 'mother',
      'area', 'money', 'story', 'fact', 'month', 'lot', 'right', 'study', 'book',
      'eye', 'job', 'word', 'business', 'issue', 'side', 'kind', 'head', 'house',
      'service', 'friend', 'father', 'power', 'hour', 'game', 'line', 'end',
      'member', 'law', 'car', 'city', 'community', 'name', 'president', 'team',
      'minute', 'idea', 'kid', 'body', 'information', 'back', 'parent', 'face',
      'others', 'level', 'office', 'door', 'health', 'person', 'art', 'war',
      'history', 'party', 'result', 'change', 'morning', 'reason', 'research',
      'girl', 'guy', 'moment', 'air', 'teacher', 'force', 'education', 'food',
      'small', 'big', 'good', 'bad', 'new', 'old', 'great', 'high', 'low',
      'long', 'short', 'large', 'young', 'different', 'important', 'little',
      'right', 'real', 'best', 'last', 'first', 'next', 'early', 'late', 'hard',
      'major', 'better', 'economic', 'strong', 'possible', 'whole', 'free',
      'sure', 'nice', 'happy', 'sorry', 'wrong', 'able', 'ready', 'full', 'easy'
    ]);

    // 1. Check for keyboard mashing / random characters
    // Look for words with repeated characters (like "hhhhhh" or "dfkajdg")
    let gibberishWordCount = 0;
    let longGibberishFound = false;
    
    for (const word of words) {
      const w = word.toLowerCase();
      
      // Skip short words and common words
      if (w.length <= 3 || commonWords.has(w)) continue;
      
      // Check for repeated characters (3+ in a row)
      if (/(.)\1{2,}/.test(w)) {
        gibberishWordCount++;
        if (w.length > 10) longGibberishFound = true;
        continue;
      }
      
      // Check for unlikely consonant clusters (no vowels in long stretch)
      if (w.length >= 5 && !/[aeiou]/.test(w)) {
        gibberishWordCount++;
        continue;
      }
      
      // Check for very long "words" with no real pattern
      if (w.length > 12 && !commonWords.has(w)) {
        // Check vowel/consonant ratio
        const vowels = (w.match(/[aeiou]/g) || []).length;
        const ratio = vowels / w.length;
        if (ratio < 0.15 || ratio > 0.7) {
          gibberishWordCount++;
          if (w.length > 15) longGibberishFound = true;
        }
      }
    }

    // Calculate gibberish ratio
    const gibberishRatio = gibberishWordCount / Math.max(words.length, 1);
    
    if (longGibberishFound) {
      penalty += 0.4;
    }
    
    if (gibberishRatio > 0.3) {
      penalty += 0.35;
    } else if (gibberishRatio > 0.2) {
      penalty += 0.25;
    } else if (gibberishRatio > 0.1) {
      penalty += 0.15;
    }

    // 2. Check for low English word ratio
    let englishWordCount = 0;
    for (const word of words) {
      if (commonWords.has(word.toLowerCase())) {
        englishWordCount++;
      }
    }
    
    const englishRatio = englishWordCount / Math.max(words.length, 1);
    
    // If very few recognizable English words, it's likely gibberish
    if (words.length >= 10 && englishRatio < 0.2) {
      penalty += 0.3;
    } else if (words.length >= 10 && englishRatio < 0.35) {
      penalty += 0.15;
    }

    // 3. Check for random letter sequences (keyboard mashing)
    const keyboardPatterns = /[qwerty]{5,}|[asdfgh]{5,}|[zxcvbn]{5,}|[uiop]{4,}|[jkl]{4,}/gi;
    if (keyboardPatterns.test(text)) {
      penalty += 0.2;
    }

    // 4. Check for excessive repeated single characters
    const repeatedChars = text.match(/(.)\1{4,}/g) || [];
    if (repeatedChars.length > 0) {
      penalty += Math.min(0.3, repeatedChars.length * 0.1);
    }

    return Math.min(0.6, penalty); // Cap gibberish penalty at 60%
  }

  _assessResponseValidity(text, analysis = {}) {
    const words = String(text || '').toLowerCase().match(/[a-z]+(?:'[a-z]+)?/g) || [];
    const gibberishPenalty = this._detectGibberish(String(text || ''), words);
    const valid = gibberishPenalty < 0.5;
    return {
      valid,
      code: valid ? 'SCORABLE_LANGUAGE' : 'UNSCORABLE_GIBBERISH',
      confidence: valid ? null : 'high',
      gibberishPenalty,
      wordCount: Number(analysis.wordCount ?? words.length),
      message: valid
        ? 'The response contains scorable language.'
        : 'The response appears to contain keyboard-mashing or unrecognizable language. Write complete, meaningful sentences and submit again.'
    };
  }

  _invalidResponseResult(text, criteria, analysis, responseValidity) {
    const scores = {};
    for (const criterion of criteria) {
      scores[criterion.name] = {
        rawScore: 0,
        points: 0,
        maxPoints: criterion.weight,
        feedback: 'Not scored because the response does not contain enough recognizable language.',
        details: {}
      };
    }
    const maxPossible = criteria.reduce((sum, criterion) => sum + (criterion.weight || 0), 0);
    return {
      scores,
      total: 0,
      maxPossible,
      percentage: 0,
      evidenceScore: 0,
      overallFeedback: responseValidity.message,
      strengths: [],
      areasForImprovement: criteria.map(criterion => criterion.name),
      suggestions: ['Write complete, meaningful sentences that directly answer the prompt.'],
      wordCount: Number(analysis.wordCount || 0),
      sentenceCount: Number(analysis.sentenceCount || 0),
      repetitionPenalty: Number(analysis.repetitionPenalty || 0),
      relevancePenalty: 0,
      requirementsPenalty: 0,
      requirementsMet: [],
      requirementsMissing: [],
      entityPenalty: 0,
      entitiesFound: { names: [], places: [], objects: [] },
      entitiesMissing: [],
      passageEntities: this.passageEntities,
      questionType: this.questionType,
      originalityScore: 0,
      coherenceScore: 0,
      analysisMode: this.trainingMode,
      responseValidity,
      writingProfiles: this._buildWritingProfiles({
        text,
        embed: null,
        analysis,
        rubricCriteria: criteria,
        holisticPercentage: 0,
        responseValidity
      })
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // QUESTION REQUIREMENTS CHECKING
  // ═══════════════════════════════════════════════════════════════════════════

  _checkRequirements(studentText, analysis) {
    const results = {
      metRequirements: [],
      missingRequirements: [],
      penalty: 0
    };

    // If no requirements extracted, no penalty
    if (this.questionRequirements.length === 0) {
      return results;
    }

    const lower = studentText.toLowerCase();

    for (const req of this.questionRequirements) {
      let met = false;

      switch (req.type) {
        // Narrative elements
        case 'sensory_details':
          met = this._hasSensoryDetails(lower);
          break;
        case 'dialogue':
          met = this._hasDialogue(studentText);
          break;
        case 'description':
          met = this._hasDescription(lower);
          break;
        case 'emotion':
          met = this._hasEmotion(lower);
          break;
        case 'lesson':
          met = /\b(learned|realized|understood|knew|discovered|found out|taught)\b/.test(lower);
          break;
        case 'character_response':
          met = this._hasCharacterResponse(lower);
          break;
        case 'climax':
          met = this._hasClimax(lower);
          break;
        case 'conclusion':
          met = this._hasConclusion(lower);
          break;
        case 'intensity':
          met = this._hasIntensity(lower);
          break;
        case 'sequence':
          met = this._hasSequence(lower);
          break;

        // Text evidence
        case 'text_evidence':
          met = this._hasTextEvidence(lower, studentText);
          break;
        case 'passage_reference':
          met = /\b(the (text|passage|story|article)|according to)\b/.test(lower);
          break;

        // Argumentative
        case 'claim':
          met = analysis?.argument?.hasClaim || /\b(i (think|believe)|should|must|argue)\b/.test(lower);
          break;
        case 'support':
          met = /\b(because|since|therefore|for example|evidence|reason)\b/.test(lower);
          break;
        case 'examples':
          met = /\b(for example|for instance|such as|one example|another example)\b/.test(lower);
          break;

        // Revision - maintain content
        case 'maintain_content':
          met = this._maintainsContent(studentText);
          break;
        case 'fix_errors':
          met = true; // Hard to verify automatically, assume attempted
          break;
        case 'fix_caps':
        case 'fix_punct':
        case 'fix_spelling':
        case 'fix_grammar':
        case 'fix_structure':
          met = true; // Hard to verify automatically
          break;

        // Quantity requirements
        case 'three_items':
        case 'minimum_count':
          // Check if they have at least 3 of the relevant items
          met = this._meetsMinimumCount(lower, studentText, 3);
          break;

        default:
          met = true; // Unknown requirement, don't penalize
      }

      if (met) {
        results.metRequirements.push(req);
      } else {
        results.missingRequirements.push(req);
      }
    }

    // Calculate penalty based on missing requirements
    const totalReqs = this.questionRequirements.length;
    const missingCount = results.missingRequirements.length;
    
    if (totalReqs > 0 && missingCount > 0) {
      // More missing = higher penalty
      results.penalty = Math.min(0.5, (missingCount / totalReqs) * 0.6);
      
      // Special case: revision task that doesn't maintain content
      if (this.questionType === 'revision' && 
          results.missingRequirements.some(r => r.type === 'maintain_content')) {
        results.penalty = Math.max(results.penalty, 0.5);
      }
    }

    console.log(`[WritingAnalyzer] Requirements check: ${results.metRequirements.length} met, ${results.missingRequirements.length} missing, penalty: ${results.penalty}`);
    
    return results;
  }

  // Helper methods for requirement checking
  _hasSensoryDetails(lower) {
    let count = 0;
    // Check each sense
    const senses = {
      sight: /\b(saw|looked|watched|bright|dark|shadow|light|color|red|blue|green)\b/,
      sound: /\b(heard|sound|noise|loud|quiet|whisper|shout|echo)\b/,
      smell: /\b(smell|scent|odor|fragrance|aroma|stench)\b/,
      touch: /\b(felt|touched|smooth|rough|cold|hot|warm|soft|hard)\b/,
      taste: /\b(taste|tasted|sweet|sour|bitter|salty|delicious)\b/
    };
    for (const pattern of Object.values(senses)) {
      if (pattern.test(lower)) count++;
    }
    return count >= 2; // At least 2 different senses
  }

  _hasDialogue(text) {
    const quotes = text.match(/"[^"]+"|"[^"]+"|'[^']{5,}'/g) || [];
    return quotes.length >= 1;
  }

  _hasDescription(lower) {
    // Check for descriptive adjectives
    const descriptive = /\b(old|young|big|small|tall|short|dark|bright|beautiful|ugly|ancient|modern|huge|tiny|massive|little|colorful|dull|shiny|dusty|clean|dirty)\b/;
    return descriptive.test(lower);
  }

  _hasEmotion(lower) {
    const emotions = /\b(felt|feeling|scared|afraid|happy|sad|angry|excited|nervous|worried|relieved|proud|ashamed|surprised|shocked|terrified|thrilled|devastated|joyful|anxious|confident)\b/;
    return emotions.test(lower);
  }

  _hasCharacterResponse(lower) {
    const response = /\b(said|replied|answered|shouted|whispered|asked|decided|thought|realized|understood|knew|acted|did|made|chose)\b/;
    return response.test(lower);
  }

  _hasClimax(lower) {
    const climax = /\b(suddenly|then|but then|that's when|in that moment|everything changed|chaos|panic|explosion|crash|scream)\b/;
    return climax.test(lower);
  }

  _hasConclusion(lower) {
    const conclusion = /\b(finally|at last|in the end|eventually|realized|learned|understood|from that day|never forget|would always)\b/;
    return conclusion.test(lower);
  }

  _hasIntensity(lower) {
    const intensity = /\b(heart (pounded|raced)|trembled|shook|screamed|shouted|ran|rushed|panic|chaos|desperate|urgent|exploded|crashed)\b/;
    return intensity.test(lower);
  }

  _hasSequence(lower) {
    const sequence = /\b(first|then|next|after|before|finally|meanwhile|suddenly|later|when|while)\b/;
    const matches = lower.match(sequence) || [];
    return matches.length >= 2;
  }

  _hasTextEvidence(lower, original) {
    // Check for citation phrases or quotes
    const citationPhrases = /\b(the text|the passage|according to|the author|states that|shows that)\b/;
    const hasQuotes = /"[^"]+"|"[^"]+"/.test(original);
    return citationPhrases.test(lower) || hasQuotes;
  }

  _maintainsContent(studentText) {
    // For revision tasks, check if student text contains key words from source
    if (!this.sourceContent || this.sourceContent.length < 20) return true;

    const sourceWords = new Set(
      this.sourceContent.toLowerCase().match(/\b[a-z]{3,}\b/g) || []
    );
    const studentWords = studentText.toLowerCase().match(/\b[a-z]{3,}\b/g) || [];

    let overlap = 0;
    for (const word of studentWords) {
      if (sourceWords.has(word)) overlap++;
    }

    const overlapRatio = overlap / Math.max(studentWords.length, 1);
    return overlapRatio >= 0.2; // At least 20% word overlap
  }

  _meetsMinimumCount(lower, original, minCount) {
    // Try to count sensory details, examples, or other countable items
    let count = 0;
    
    // Count sensory details
    const senses = ['saw', 'heard', 'felt', 'smell', 'taste', 'looked', 'sound', 'touch'];
    for (const sense of senses) {
      if (lower.includes(sense)) count++;
    }
    
    // Count examples
    const examples = (lower.match(/\b(for example|for instance|such as|like|another)\b/g) || []).length;
    count += examples;
    
    return count >= minCount;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OFF-TOPIC / IRRELEVANT CONTENT DETECTION
  // ═══════════════════════════════════════════════════════════════════════════

  _detectOffTopic(studentText, studentEmbed) {
    // IMPORTANT: For narrative/creative writing, students write NEW stories
    // They should NOT be penalized for not matching passage words
    // Only revision tasks require matching the source content
    if (this.questionType === 'narrative' || this.questionType === 'general') {
      // For narrative tasks, check against QUESTION keywords instead of passage
      return this._checkQuestionRelevance(studentText);
    }
    
    // For revision tasks, we DO want to check passage overlap
    if (this.questionType !== 'revision' && this.questionType !== 'text-based') {
      // For argumentative/expository that references passage, use lighter check
      return this._checkQuestionRelevance(studentText) * 0.5;
    }

    // If no source content to compare against, can't detect off-topic
    if (!this.sourceContent || this.sourceContent.length < 50) {
      return 0;
    }

    let penalty = 0;
    const studentLower = studentText.toLowerCase();
    const sourceLower = this.sourceContent.toLowerCase();

    // 1. Extract key content words from source (nouns, names, unique terms)
    const sourceWords = sourceLower.match(/\b[a-z]{4,}\b/g) || [];
    const studentWords = studentLower.match(/\b[a-z]{4,}\b/g) || [];
    
    // Get unique meaningful words from source (excluding common words)
    const meaningfulSourceWords = new Set(
      sourceWords.filter(w => !this._isStopWord(w) && w.length >= 4)
    );
    
    // Count how many source words appear in student text
    let overlapCount = 0;
    for (const word of meaningfulSourceWords) {
      if (studentLower.includes(word)) {
        overlapCount++;
      }
    }
    
    const overlapRatio = overlapCount / Math.max(meaningfulSourceWords.size, 1);
    
    // 2. Check for completely different topic
    // If very few source words appear, likely off-topic
    if (meaningfulSourceWords.size >= 5) {
      if (overlapRatio < 0.1) {
        penalty += 0.5; // Severe: almost no overlap
        console.log('[WritingAnalyzer] Off-topic: very low word overlap:', overlapRatio);
      } else if (overlapRatio < 0.2) {
        penalty += 0.35; // Significant: minimal overlap
        console.log('[WritingAnalyzer] Likely off-topic: low word overlap:', overlapRatio);
      } else if (overlapRatio < 0.3) {
        penalty += 0.2; // Moderate: some overlap missing
      }
    }

    // 3. Neural similarity check
    if (studentEmbed && this.passageEmbeddings.length > 0) {
      let maxSim = 0;
      for (const passageEmbed of this.passageEmbeddings) {
        const sim = cosineSimilarity(studentEmbed, passageEmbed);
        if (sim > maxSim) maxSim = sim;
      }
      
      // Very low similarity = likely off-topic
      if (maxSim < 0.2) {
        penalty += 0.3;
        console.log('[WritingAnalyzer] Off-topic: very low neural similarity:', maxSim);
      } else if (maxSim < 0.3) {
        penalty += 0.15;
      }
    }

    // 4. Check for key entities/names from source
    // Extract capitalized words (likely names/places) from source
    const sourceNames = this.sourceContent.match(/\b[A-Z][a-z]+\b/g) || [];
    const uniqueSourceNames = new Set(sourceNames.filter(n => n.length >= 3));
    
    if (uniqueSourceNames.size >= 2) {
      let nameMatches = 0;
      for (const name of uniqueSourceNames) {
        if (studentText.includes(name)) {
          nameMatches++;
        }
      }
      
      // If source has names but student text has none of them
      if (nameMatches === 0 && uniqueSourceNames.size >= 3) {
        penalty += 0.2;
        console.log('[WritingAnalyzer] Off-topic: no matching names/entities');
      }
    }

    return Math.min(0.6, penalty); // Cap at 60%
  }

  // Check if response is relevant to the QUESTION (not passage)
  // Used for narrative/creative writing where students write NEW content
  _checkQuestionRelevance(studentText) {
    // If no question keywords, no penalty
    if (!this.questionKeywords || this.questionKeywords.size === 0) {
      return 0;
    }

    const studentLower = studentText.toLowerCase();
    let penalty = 0;

    // Check if student text contains question keywords
    let keywordMatches = 0;
    for (const keyword of this.questionKeywords) {
      if (studentLower.includes(keyword)) {
        keywordMatches++;
      }
    }

    const keywordRatio = keywordMatches / Math.max(this.questionKeywords.size, 1);

    // For narrative tasks, we're more lenient
    // Students just need to write SOMETHING relevant to the prompt
    // We check if they included key elements mentioned in the question
    
    // Check for question entities (names mentioned in prompt)
    let entityMatches = 0;
    for (const entity of this.questionEntities) {
      if (studentText.includes(entity) || studentLower.includes(entity.toLowerCase())) {
        entityMatches++;
      }
    }

    // Very lenient: only penalize if NOTHING from the question appears
    // and the response is very short or clearly unrelated
    if (keywordMatches === 0 && entityMatches === 0 && this.questionKeywords.size >= 3) {
      // Check if it's at least a story/narrative
      const hasNarrativeElements = this._hasNarrativeElements(studentLower);
      if (!hasNarrativeElements) {
        penalty = 0.3; // Not even a story
        console.log('[WritingAnalyzer] Response lacks narrative elements');
      }
      // If it IS a story, don't penalize - they wrote a valid narrative
    }

    return penalty;
  }

  // Check if text has basic narrative elements
  _hasNarrativeElements(lower) {
    // Check for story indicators
    const hasCharacter = /\b(he|she|they|i|we|him|her|them|his|hers|their|my|our)\b/.test(lower);
    const hasAction = /\b(walked|ran|said|went|came|looked|saw|felt|thought|knew|wanted|tried|made|took|got)\b/.test(lower);
    const hasSequence = /\b(then|when|after|before|suddenly|finally|first|next)\b/.test(lower);
    const hasDialogue = /"[^"]+"|"[^"]+"/.test(lower) || /\b(said|asked|replied|shouted|whispered)\b/.test(lower);
    
    // Count how many elements are present
    let count = 0;
    if (hasCharacter) count++;
    if (hasAction) count++;
    if (hasSequence) count++;
    if (hasDialogue) count++;
    
    return count >= 2; // At least 2 narrative elements
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COHERENCE
  // ═══════════════════════════════════════════════════════════════════════════

  _coherence(sentences) {
    if (sentences.length < 2) return 0.5;

    let score = 0.5;

    // Check transitions
    let trans = 0;
    for (let i = 1; i < sentences.length; i++) {
      const s = sentences[i].toLowerCase();
      for (const ws of Object.values(TRANSITIONS)) {
        if (ws.some(w => s.startsWith(w) || s.includes(`, ${w}`))) {
          trans++;
          break;
        }
      }
    }
    score += Math.min(0.25, trans * 0.05);

    // Neural coherence
    if (isModelTrained() && sentences.length >= 3) {
      try {
        let sim = 0;
        const lim = Math.min(sentences.length, 8);
        for (let i = 1; i < lim; i++) {
          sim += cosineSimilarity(forward(encode(sentences[i - 1])).h2, forward(encode(sentences[i])).h2);
        }
        score += (sim / (lim - 1)) * 0.2;
      } catch (e) {}
    }

    return Math.min(1, score);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CRITERION SCORING
  // ═══════════════════════════════════════════════════════════════════════════

  _scoreCriterion(text, embed, criterion, analysis) {
    const name = (criterion.name || '').toLowerCase();
    const weight = criterion.weight || 1;
    let r;

    switch (name) {
      // === ARGUMENTATIVE/EXPOSITORY CRITERIA ===
      case 'claim': case 'thesis': case 'position':
        r = this._scoreClaim(text, analysis); break;
      case 'reasoning': case 'logic': case 'analysis':
        r = this._scoreReasoning(text, analysis); break;
      case 'evidence': case 'support': case 'examples':
        r = this._scoreEvidence(text, embed); break;
      case 'coherence': case 'organization': case 'structure':
        r = this._scoreCoherence(text, analysis); break;
      case 'conventions': case 'grammar': case 'mechanics':
        r = this._scoreConventions(text); break;
      case 'vocabulary': case 'word choice': case 'language':
        r = this._scoreVocabulary(analysis); break;
      case 'transitions': case 'transition use':
        r = this._scoreTransitions(text); break;
      case 'conclusion': case 'closing':
        r = this._scoreConclusion(text); break;
      case 'introduction': case 'hook': case 'opening':
        r = this._scoreIntro(text); break;
      case 'development': case 'elaboration':
        r = this._scoreDevelopment(text, analysis); break;
      case 'sentence fluency': case 'fluency':
        r = this._scoreFluency(analysis); break;
      
      // === NARRATIVE/CREATIVE WRITING CRITERIA ===
      case 'narrative development': case 'narrative': case 'story development': case 'plot':
        r = this._scoreNarrativeDevelopment(text, analysis); break;
      case 'descriptive details': case 'descriptive': case 'description': case 'details':
        r = this._scoreDescriptiveDetails(text); break;
      case 'dialogue': case 'dialog': case 'conversation':
        r = this._scoreDialogue(text); break;
      case 'sensory details': case 'sensory': case 'sensory language':
        r = this._scoreSensoryDetails(text); break;
      case 'concrete words': case 'concrete': case 'specific words': case 'specific details': case 'concrete nouns':
        r = this._scoreConcreteWords(text); break;
      case 'word precision': case 'precise words': case 'precision': case 'precise language':
        r = this._scoreWordPrecision(text); break;
      case 'vivid verbs': case 'vivid language': case 'vivid words': case 'strong verbs':
        r = this._scoreVividVerbs(text); break;
      case 'reader experience': case 'immersion': case 'reader engagement':
        r = this._scoreReaderExperience(text); break;
      case 'clarity': case 'clear writing': case 'clearness':
        r = this._scoreClarity(text); break;
      case 'suspense': case 'suspense building': case 'tension':
        r = this._scoreSuspense(text); break;
      case 'pacing': case 'pacing control': case 'pace':
        r = this._scorePacing(text); break;
      case 'character': case 'character development': case 'characterization':
        r = this._scoreCharacter(text); break;
      case 'character introduction': case 'introducing character': case 'character intro':
        r = this._scoreCharacterIntroduction(text); break;
      case 'setting': case 'atmosphere': case 'mood':
        r = this._scoreSetting(text); break;
      case 'situation': case 'story situation': case 'context':
        r = this._scoreSituation(text); break;
      case 'figurative language': case 'figurative': case 'imagery':
        r = this._scoreFigurativeLanguage(text); break;
      case 'sequence': case 'sequence of events': case 'event sequence': case 'sequence clarity':
        r = this._scoreSequence(text); break;
      case 'resolution': case 'ending': case 'story ending':
        r = this._scoreResolution(text); break;
      case 'character growth': case 'character change': case 'arc':
        r = this._scoreCharacterGrowth(text); break;
      case 'satisfaction': case 'reader satisfaction': case 'completeness':
        r = this._scoreSatisfaction(text, analysis); break;
      case 'event development': case 'events': case 'action':
        r = this._scoreEventDevelopment(text); break;
      case 'transition use': case 'transitions use': case 'using transitions':
        r = this._scoreTransitions(text); break;
      
      // === CLIMAX / INTENSITY CRITERIA ===
      case 'intensity': case 'intense moment': case 'excitement': case 'tension level':
        r = this._scoreIntensity(text); break;
      case 'character action': case 'character actions': case 'actions taken':
        r = this._scoreCharacterAction(text); break;
      case 'emotion': case 'emotions': case 'emotional response': case 'feelings':
        r = this._scoreEmotion(text); break;
      case 'conflict': case 'problem': case 'challenge':
        r = this._scoreConflict(text); break;
      case 'stakes': case 'high stakes': case 'consequences':
        r = this._scoreStakes(text); break;
      case 'turning point': case 'climax moment': case 'peak':
        r = this._scoreTurningPoint(text); break;
      
      // === REVISION / EDITING CRITERIA ===
      case 'error correction': case 'corrections': case 'fixing errors':
        r = this._scoreErrorCorrection(text, embed); break;
      case 'capitalization': case 'capital letters': case 'caps':
        r = this._scoreCapitalization(text); break;
      case 'sentence structure': case 'sentence construction': case 'complete sentences':
        r = this._scoreSentenceStructure(text); break;
      case 'clarity': case 'clear writing': case 'clearness':
        r = this._scoreClarity(text); break;
      case 'spelling': case 'spell check': case 'correct spelling':
        r = this._scoreSpelling(text); break;
      case 'punctuation': case 'punctuation marks': case 'proper punctuation':
        r = this._scorePunctuation(text); break;
      
      // === NARRATIVE INTEGRATION / DIALOGUE QUALITY ===
      case 'dialogue quality': case 'dialogue use': case 'natural dialogue':
        r = this._scoreDialogueQuality(text); break;
      case 'narrative integration': case 'dialogue description integration': case 'blending':
        r = this._scoreNarrativeIntegration(text); break;
      
      // === TEXT EVIDENCE / CITATION CRITERIA ===
      case 'text evidence': case 'textual evidence': case 'citing evidence': case 'citations':
        r = this._scoreTextEvidence(text, embed); break;
      case 'quotes': case 'quotations': case 'direct quotes': case 'quoting':
        r = this._scoreQuotes(text); break;
      case 'paraphrasing': case 'paraphrase': case 'restating':
        r = this._scoreParaphrasing(text, embed); break;
      case 'text reference': case 'reference': case 'referencing text': case 'passage reference':
        r = this._scoreTextReference(text, embed); break;
      case 'evidence integration': case 'integrating evidence':
        r = this._scoreEvidenceIntegration(text); break;
      case 'integration':
        // Check question type to determine which integration to use
        if (this.questionType === 'narrative' || this.questionType === 'general') {
          r = this._scoreNarrativeIntegration(text);
        } else {
          r = this._scoreEvidenceIntegration(text);
        }
        break;
        
      default:
        r = this._scoreGeneric(text, embed, criterion);
    }

    r.points = Math.round(r.rawScore * weight * 10) / 10;
    r.maxPoints = weight;
    return r;
  }

  _scoreClaim(text, analysis) {
    let s = 0;
    const d = { indicators: [] };

    for (const p of CLAIM_PATTERNS.strong) {
      if (p.test(text)) { s += 0.3; d.indicators.push('Strong claim language'); break; }
    }
    for (const p of CLAIM_PATTERNS.moderate) {
      if (p.test(text)) { s += 0.2; d.indicators.push('Opinion language'); break; }
    }

    const sents = this._getSentences(text);
    if (sents.length > 0 && sents[0].length > 40 && /\b(should|must|important|argue|believe)\b/i.test(sents[0])) {
      s += 0.25;
      d.indicators.push('Thesis-like opening');
    }

    if (/\b(support|oppose|agree|disagree)\b/i.test(text)) {
      s += 0.15;
      d.indicators.push('Clear position');
    }

    if (analysis?.argument?.hasClaim) s += 0.1;

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong, clear claim.' : s >= 0.4 ? 'Claim present but could be stronger.' : 'Needs a clear claim or thesis.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreReasoning(text, analysis) {
    let s = 0;
    const d = { indicators: [] };

    for (const [t, ps] of Object.entries(REASONING_PATTERNS)) {
      if (ps.some(p => p.test(text))) {
        s += 0.15;
        d.indicators.push(`${t} reasoning`);
      }
    }

    const logical = ['because', 'therefore', 'since', 'thus', 'consequently'].filter(w => text.toLowerCase().includes(w)).length;
    s += Math.min(0.25, logical * 0.08);
    if (logical > 0) d.indicators.push('Logical connectors');

    if (analysis?.argument?.hasReasoning) s += 0.1;

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong reasoning with clear logic.' : s >= 0.4 ? 'Reasoning present but needs development.' : 'Needs more "why" explanation.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreEvidence(text, embed) {
    let s = 0;
    const d = { indicators: [] };

    for (const [t, ps] of Object.entries(EVIDENCE_PATTERNS)) {
      if (ps.some(p => p.test(text))) {
        s += t === 'quotation' ? 0.25 : 0.2;
        d.indicators.push(`${t} evidence`);
        break;
      }
    }

    const quotes = text.match(/[""][^""]{10,}[""]/g) || [];
    if (quotes.length > 0) {
      s += Math.min(0.25, quotes.length * 0.12);
      d.indicators.push(`${quotes.length} quote(s)`);
    }

    if (embed && this.passageEmbeddings.length > 0) {
      let max = 0;
      for (const e of this.passageEmbeddings) {
        const sim = cosineSimilarity(embed, e);
        if (sim > max) max = sim;
      }
      s += max * 0.25;
      if (max > 0.5) d.indicators.push('References passage content');
    }

    if (this.sourceKeywords.size > 0) {
      const tw = new Set(text.toLowerCase().split(/\s+/));
      const overlap = [...this.sourceKeywords].filter(k => tw.has(k)).length;
      s += Math.min(0.15, overlap * 0.02);
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong evidence from the text.' : s >= 0.4 ? 'Some evidence, needs more support.' : 'Needs more evidence from the passage.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreCoherence(text, analysis) {
    let s = analysis?.coherence || 0.3;
    const d = { indicators: [] };

    const paras = text.split(/\n\n+/).filter(p => p.trim().length > 30);
    if (paras.length >= 2) {
      s += 0.15;
      d.indicators.push('Multiple paragraphs');
    }

    let trans = 0;
    for (const ws of Object.values(TRANSITIONS)) {
      trans += ws.filter(w => text.toLowerCase().includes(w)).length;
    }
    if (trans > 0) {
      s += Math.min(0.2, trans * 0.04);
      d.indicators.push('Uses transitions');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Well-organized and coherent.' : s >= 0.4 ? 'Mostly coherent.' : 'Organization needs work.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreConventions(text) {
    let s = 1;
    const d = { errors: [], errorCount: 0 };

    for (const { wrong, right, type } of COMMON_ERRORS) {
      const m = text.match(wrong);
      if (m) {
        s -= m.length * (type === 'grammar' ? 0.05 : 0.03);
        d.errors.push({ found: m[0], correction: right });
        d.errorCount += m.length;
      }
    }

    const afterPeriod = text.match(/\.\s+[a-z]/g) || [];
    if (afterPeriod.length > 0) {
      s -= afterPeriod.length * 0.03;
      d.errors.push({ type: 'capitalization', count: afterPeriod.length });
    }

    const runOns = text.split(/[.!?]/).filter(x => x.split(/\s+/).length > 40);
    if (runOns.length > 0) {
      s -= runOns.length * 0.06;
      d.errors.push({ type: 'run-on', count: runOns.length });
    }

    s = Math.max(0, s);
    const feedback = s >= 0.9 ? 'Excellent conventions.' : s >= 0.7 ? 'Good conventions with minor errors.' : s >= 0.5 ? 'Some errors need correction.' : 'Multiple errors need attention.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreGrammar(text) {
    let s = 1;
    const d = { errors: [], errorCount: 0 };
    const grammarPatterns = [
      ...COMMON_ERRORS.filter(error => error.type === 'grammar').map(error => ({
        pattern: error.wrong,
        label: `Use "${error.right}"`
      })),
      { pattern: /\b(he|she|it)\s+(are|were|have)\b/gi, label: 'Possible subject-verb agreement error' },
      { pattern: /\b(they|we)\s+(is|was|has)\b/gi, label: 'Possible subject-verb agreement error' },
      { pattern: /\bI\s+(is|are)\b/g, label: 'Possible subject-verb agreement error' },
      { pattern: /\b(doesn't|does not)\s+\w+s\b/gi, label: 'Possible verb-form error after does not' },
      { pattern: /\b(didn't|did not)\s+\w+(?:ed)\b/gi, label: 'Possible verb-form error after did not' }
    ];

    for (const { pattern, label } of grammarPatterns) {
      pattern.lastIndex = 0;
      const matches = text.match(pattern) || [];
      if (!matches.length) continue;
      d.errorCount += matches.length;
      d.errors.push({ type: 'grammar', example: matches[0], message: label, count: matches.length });
    }

    s -= Math.min(0.8, d.errorCount * 0.1);
    s = boundedScore(s);
    const feedback = d.errorCount === 0
      ? 'No checked grammar patterns were detected.'
      : s >= 0.8
        ? 'A small number of possible grammar errors were detected.'
        : s >= 0.6
          ? 'Review several possible grammar errors.'
          : 'Grammar needs careful review.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreVocabulary(analysis) {
    const v = analysis?.vocabulary || { score: 0.5, level: 'adequate', suggestions: [] };
    const feedback = v.level === 'sophisticated' ? 'Excellent vocabulary.' : v.level === 'proficient' ? 'Good word choices.' : v.level === 'adequate' ? 'Adequate vocabulary.' : 'Try using more varied vocabulary.';
    return { rawScore: v.score, feedback, details: { level: v.level, advancedWords: v.advancedWords, suggestions: v.suggestions } };
  }

  _scoreTransitions(text) {
    let s = 0.15; // Base score
    const d = { found: [], categories: {}, totalCount: 0 };
    const lower = text.toLowerCase();

    // Check each category of transitions
    for (const [cat, ws] of Object.entries(TRANSITIONS)) {
      d.categories[cat] = 0;
      for (const w of ws) {
        // Use regex for word boundary matching
        const regex = new RegExp(`\\b${w.replace(/\s+/g, '\\s+')}\\b`, 'gi');
        const matches = lower.match(regex) || [];
        if (matches.length > 0) {
          d.found.push(w);
          d.categories[cat] += matches.length;
          d.totalCount += matches.length;
        }
      }
    }

    // Score based on total transitions found
    if (d.totalCount >= 10) { s += 0.4; }
    else if (d.totalCount >= 7) { s += 0.35; }
    else if (d.totalCount >= 5) { s += 0.3; }
    else if (d.totalCount >= 4) { s += 0.25; }
    else if (d.totalCount >= 3) { s += 0.2; }
    else if (d.totalCount >= 2) { s += 0.15; }
    else if (d.totalCount >= 1) { s += 0.1; }

    // Bonus for variety (using multiple categories)
    const categoriesUsed = Object.values(d.categories).filter(c => c > 0).length;
    if (categoriesUsed >= 4) { s += 0.25; d.found.push('(excellent variety)'); }
    else if (categoriesUsed >= 3) { s += 0.15; d.found.push('(good variety)'); }
    else if (categoriesUsed >= 2) { s += 0.1; }

    // Extra bonus for time-order transitions (important for narrative)
    if (d.categories.sequence >= 4) { s += 0.1; }
    if (d.categories.time >= 1) { s += 0.05; }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent use of transitions!' : s >= 0.55 ? 'Good transitions.' : s >= 0.35 ? 'Some transitions, try adding more variety.' : 'Add more transition words to guide your reader.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreConclusion(text) {
    let s = 0;
    const d = { indicators: [] };

    for (const sig of TRANSITIONS.conclusion) {
      if (text.toLowerCase().includes(sig)) {
        s += 0.25;
        d.indicators.push('Conclusion signal word');
        break;
      }
    }

    const sents = this._getSentences(text);
    if (sents.length >= 2) {
      const last = sents[sents.length - 1].toLowerCase();
      if (/\b(therefore|thus|shows|proves|important)\b/.test(last)) {
        s += 0.25;
        d.indicators.push('Reinforces main point');
      }
      if (/\b(should|must|everyone|society|future)\b/.test(last)) {
        s += 0.2;
        d.indicators.push('Broader implication');
      }
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong conclusion.' : s >= 0.4 ? 'Conclusion could be stronger.' : 'Needs a clear conclusion.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreIntro(text) {
    let s = 0;
    const d = { indicators: [] };

    const sents = this._getSentences(text);
    if (sents.length === 0) return { rawScore: 0, feedback: 'No introduction found.', details: d };

    const first = sents[0];
    const firstPara = text.split(/\n\n/)[0] || text;

    if (SENTENCE_STARTERS.engaging.some(p => p.test(first))) {
      s += 0.25;
      d.indicators.push('Engaging hook');
    }

    if (firstPara.length > 100) {
      s += 0.2;
      d.indicators.push('Provides context');
    }

    if (/\b(argue|believe|think|claim|will show)\b/i.test(firstPara)) {
      s += 0.3;
      d.indicators.push('Thesis statement');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong introduction.' : s >= 0.4 ? 'Introduction could be more engaging.' : 'Needs a clearer introduction.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreDevelopment(text, analysis) {
    let s = 0;
    const d = { indicators: [] };

    const words = text.match(/\b[a-zA-Z]+\b/g) || [];
    if (words.length >= 100) { s += 0.15; d.indicators.push('Sufficient length'); }
    if (words.length >= 200) s += 0.1;

    if (this._getSentences(text).length >= 5) {
      s += 0.1;
      d.indicators.push('Multiple sentences');
    }

    if (/\bfor example\b|\bfor instance\b|\bsuch as\b/i.test(text)) {
      s += 0.2;
      d.indicators.push('Includes examples');
    }

    if (/\bthis means\b|\bthis shows\b|\bin other words\b/i.test(text)) {
      s += 0.15;
      d.indicators.push('Explains ideas');
    }

    if (analysis?.argument?.components?.length >= 3) {
      s += 0.2;
      d.indicators.push('Multiple argument components');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Well-developed ideas.' : s >= 0.4 ? 'Ideas need more development.' : 'Needs more explanation and examples.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreFluency(analysis) {
    const v = analysis?.sentenceVariety || { score: 0.5, variety: 'adequate', avgLength: 15 };
    const feedback = v.variety === 'excellent' ? 'Excellent sentence fluency.' : v.variety === 'good' ? 'Good sentence variety.' : v.variety === 'adequate' ? 'Try varying sentences more.' : 'Sentences are too similar.';
    return { rawScore: v.score, feedback, details: { variety: v.variety, avgLength: v.avgLength } };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NARRATIVE/CREATIVE WRITING SCORING METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  _scoreNarrativeDevelopment(text, analysis) {
    let s = 0.3; // Base score for attempting
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Check for action verbs (story movement)
    let actionCount = 0;
    for (const pattern of NARRATIVE_PATTERNS.actions) {
      const matches = text.match(pattern) || [];
      actionCount += matches.length;
    }
    if (actionCount >= 5) { s += 0.25; d.indicators.push('Strong action sequence'); }
    else if (actionCount >= 2) { s += 0.15; d.indicators.push('Actions present'); }

    // Check for sequence words (narrative flow)
    let seqCount = 0;
    for (const pattern of NARRATIVE_PATTERNS.sequence) {
      const matches = text.match(pattern) || [];
      seqCount += matches.length;
    }
    if (seqCount >= 3) { s += 0.2; d.indicators.push('Clear sequence'); }
    else if (seqCount >= 1) { s += 0.1; d.indicators.push('Some sequence words'); }

    // Check for character reactions
    let emotionCount = 0;
    for (const pattern of NARRATIVE_PATTERNS.emotions) {
      const matches = text.match(pattern) || [];
      emotionCount += matches.length;
    }
    if (emotionCount >= 3) { s += 0.15; d.indicators.push('Character emotions shown'); }
    else if (emotionCount >= 1) { s += 0.08; d.indicators.push('Some emotion'); }

    // Bonus for longer narratives with variety
    const sentences = this._getSentences(text);
    if (sentences.length >= 4) { s += 0.1; d.indicators.push('Developed narrative'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong narrative development.' : s >= 0.5 ? 'Good story progression.' : s >= 0.3 ? 'Narrative needs more development.' : 'Add more story elements.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreDescriptiveDetails(text) {
    let s = 0.25; // Base score
    const d = { indicators: [], sensoryTypes: [] };

    // Check each sensory type
    let totalSensory = 0;
    for (const [sense, patterns] of Object.entries(NARRATIVE_PATTERNS.sensory)) {
      let count = 0;
      for (const p of patterns) {
        const matches = text.match(p) || [];
        count += matches.length;
      }
      if (count > 0) {
        totalSensory += count;
        d.sensoryTypes.push(sense);
      }
    }

    if (d.sensoryTypes.length >= 3) { s += 0.3; d.indicators.push('Multiple senses engaged'); }
    else if (d.sensoryTypes.length >= 2) { s += 0.2; d.indicators.push('Two senses used'); }
    else if (d.sensoryTypes.length >= 1) { s += 0.1; d.indicators.push('Sensory detail present'); }

    // Check for vivid words
    let vividCount = 0;
    const lower = text.toLowerCase();
    for (const word of VIVID_VERBS) if (lower.includes(word)) vividCount++;
    for (const word of VIVID_ADJECTIVES) if (lower.includes(word)) vividCount++;
    if (vividCount >= 4) { s += 0.2; d.indicators.push('Vivid vocabulary'); }
    else if (vividCount >= 2) { s += 0.1; d.indicators.push('Some vivid words'); }

    // Check for figurative language
    let figCount = 0;
    for (const p of NARRATIVE_PATTERNS.figurative) {
      const matches = text.match(p) || [];
      figCount += matches.length;
    }
    if (figCount >= 2) { s += 0.15; d.indicators.push('Uses figurative language'); }
    else if (figCount >= 1) { s += 0.08; d.indicators.push('Simile/metaphor present'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent descriptive details!' : s >= 0.5 ? 'Good use of details.' : s >= 0.3 ? 'Add more descriptive details.' : 'Needs sensory details.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreDialogue(text) {
    let s = 0.2; // Base score
    const d = { indicators: [], dialogueCount: 0 };

    // Count quoted speech - multiple patterns for different quote styles
    const doubleQuotes = text.match(/"[^"]+"/g) || [];
    const curlyQuotes = text.match(/[""][^""]+[""]/g) || [];
    const singleQuotes = text.match(/'[^']{5,}'/g) || []; // Min 5 chars to avoid contractions
    
    // Combine and deduplicate
    const allQuotes = [...doubleQuotes, ...curlyQuotes, ...singleQuotes];
    d.dialogueCount = allQuotes.length;

    // More generous scoring for dialogue
    if (d.dialogueCount >= 4) { s += 0.5; d.indicators.push('Rich dialogue'); }
    else if (d.dialogueCount >= 3) { s += 0.4; d.indicators.push('Multiple dialogue exchanges'); }
    else if (d.dialogueCount >= 2) { s += 0.35; d.indicators.push('Good dialogue use'); }
    else if (d.dialogueCount >= 1) { s += 0.25; d.indicators.push('Dialogue present'); }

    // Check for dialogue tags
    const tags = ['said', 'asked', 'replied', 'answered', 'whispered', 'shouted', 'yelled', 'exclaimed', 'muttered', 'murmured', 'demanded', 'wondered', 'called', 'cried', 'breathed'];
    let tagCount = 0;
    const lower = text.toLowerCase();
    for (const tag of tags) {
      if (lower.includes(tag)) tagCount++;
    }
    if (tagCount >= 3) { s += 0.2; d.indicators.push('Varied dialogue tags'); }
    else if (tagCount >= 2) { s += 0.15; d.indicators.push('Good dialogue tags'); }
    else if (tagCount >= 1) { s += 0.1; d.indicators.push('Dialogue attribution'); }

    // Bonus for dialogue that shows character
    if (d.dialogueCount > 0 && /\b(nervous|scared|excited|angry|surprised|curious|confused|worried)\b/i.test(text)) {
      s += 0.1;
      d.indicators.push('Dialogue reveals emotion');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent dialogue!' : s >= 0.5 ? 'Good dialogue use.' : s >= 0.3 ? 'Dialogue present.' : 'Add dialogue to your story.';
    return { rawScore: s, feedback, details: d };
  }

  // Enhanced dialogue quality scoring
  _scoreDialogueQuality(text) {
    let s = 0.2;
    const d = { indicators: [], dialogueCount: 0, naturalness: 0 };

    // Count all dialogue
    const allQuotes = text.match(/"[^"]+"|[""][^""]+[""]|'[^']{5,}'/g) || [];
    d.dialogueCount = allQuotes.length;

    // Base score for having dialogue
    if (d.dialogueCount >= 4) { s += 0.35; d.indicators.push('Multiple dialogue lines'); }
    else if (d.dialogueCount >= 2) { s += 0.25; d.indicators.push('Good dialogue amount'); }
    else if (d.dialogueCount >= 1) { s += 0.15; d.indicators.push('Dialogue present'); }

    // Check for natural dialogue (contractions, questions, exclamations)
    const lower = text.toLowerCase();
    const hasContractions = /\b(don't|can't|won't|isn't|aren't|wasn't|weren't|I'm|you're|we're|they're|he's|she's|it's|that's|what's|there's)\b/.test(text);
    const hasQuestions = /\?/.test(text);
    const hasExclamations = /!/.test(text);
    
    if (hasContractions) { s += 0.1; d.indicators.push('Natural contractions'); d.naturalness++; }
    if (hasQuestions) { s += 0.08; d.indicators.push('Questions in dialogue'); d.naturalness++; }
    if (hasExclamations) { s += 0.08; d.indicators.push('Expressive dialogue'); d.naturalness++; }

    // Check for varied dialogue tags
    const basicTags = (lower.match(/\b(said|asked)\b/g) || []).length;
    const variedTags = (lower.match(/\b(whispered|shouted|yelled|exclaimed|muttered|murmured|replied|answered|demanded|breathed|called|cried)\b/g) || []).length;
    
    if (variedTags >= 2) { s += 0.15; d.indicators.push('Varied dialogue tags'); }
    else if (variedTags >= 1) { s += 0.1; d.indicators.push('Some varied tags'); }
    else if (basicTags >= 1) { s += 0.05; d.indicators.push('Basic tags'); }

    // Check for action beats with dialogue
    const actionBeats = /[.!?][""]?\s+\w+\s+(walked|turned|looked|nodded|shook|smiled|frowned|grabbed|pointed|stepped)/i.test(text);
    if (actionBeats) { s += 0.1; d.indicators.push('Action with dialogue'); }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent dialogue quality!' : s >= 0.55 ? 'Good natural dialogue.' : s >= 0.35 ? 'Dialogue present.' : 'Add more natural dialogue.';
    return { rawScore: s, feedback, details: d };
  }

  // Score how well dialogue and description are integrated
  _scoreNarrativeIntegration(text) {
    let s = 0.25;
    const d = { indicators: [] };

    // Check for dialogue presence
    const hasDialogue = /"[^"]+"|[""][^""]+[""]/.test(text);
    
    // Check for description/sensory details
    let sensoryCount = 0;
    for (const patterns of Object.values(NARRATIVE_PATTERNS.sensory)) {
      for (const p of patterns) {
        if (p.test(text)) { sensoryCount++; break; }
      }
    }
    const hasDescription = sensoryCount >= 2;

    // Check for action verbs
    let actionCount = 0;
    for (const p of NARRATIVE_PATTERNS.actions) {
      actionCount += (text.match(p) || []).length;
    }
    const hasAction = actionCount >= 3;

    // Score integration - having multiple elements together
    if (hasDialogue && hasDescription && hasAction) {
      s += 0.4;
      d.indicators.push('Excellent integration of dialogue, description, and action');
    } else if (hasDialogue && hasDescription) {
      s += 0.3;
      d.indicators.push('Good dialogue and description blend');
    } else if (hasDialogue && hasAction) {
      s += 0.25;
      d.indicators.push('Dialogue with action');
    } else if (hasDescription && hasAction) {
      s += 0.2;
      d.indicators.push('Description with action');
    } else if (hasDialogue || hasDescription || hasAction) {
      s += 0.1;
      d.indicators.push('Some narrative elements');
    }

    // Check for smooth transitions between dialogue and description
    const dialogueThenDescription = /[""][.!?]\s+[A-Z][^"]*\b(looked|saw|felt|heard|watched|noticed)\b/i.test(text);
    const descriptionThenDialogue = /\.\s+[""][A-Z]/i.test(text);
    
    if (dialogueThenDescription && descriptionThenDialogue) {
      s += 0.15;
      d.indicators.push('Smooth transitions');
    } else if (dialogueThenDescription || descriptionThenDialogue) {
      s += 0.08;
      d.indicators.push('Some flow between elements');
    }

    // Check for action beats (character actions between/around dialogue)
    const actionBeats = text.match(/[,]\s*(his|her|my|the)\s+\w+\s+(trembling|shaking|racing|pounding)/gi) || [];
    if (actionBeats.length > 0) {
      s += 0.1;
      d.indicators.push('Action beats enhance dialogue');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent narrative integration!' : s >= 0.5 ? 'Good integration.' : s >= 0.3 ? 'Some integration.' : 'Blend dialogue and description more smoothly.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSensoryDetails(text) {
    let s = 0.2;
    const d = { indicators: [], senses: {} };

    // Score each sense
    for (const [sense, patterns] of Object.entries(NARRATIVE_PATTERNS.sensory)) {
      let count = 0;
      for (const p of patterns) count += (text.match(p) || []).length;
      d.senses[sense] = count;
      if (count >= 2) s += 0.15;
      else if (count >= 1) s += 0.08;
    }

    const sensesUsed = Object.values(d.senses).filter(c => c > 0).length;
    if (sensesUsed >= 4) { s += 0.15; d.indicators.push('Rich sensory experience'); }
    else if (sensesUsed >= 3) { s += 0.1; d.indicators.push('Multiple senses'); }
    else if (sensesUsed >= 2) { s += 0.05; d.indicators.push('Two senses used'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Vivid sensory details!' : s >= 0.5 ? 'Good sensory language.' : s >= 0.3 ? 'Add more sensory details.' : 'Engage the reader\'s senses.';
    return { rawScore: s, feedback, details: d };
  }

  // Score use of concrete/specific words vs vague language
  _scoreConcreteWords(text) {
    let s = 0.2;
    const d = { indicators: [], concreteWords: [], vagueWords: [] };
    const lower = text.toLowerCase();

    // Concrete nouns (specific things you can see/touch)
    const concreteNouns = [
      // Foods
      'soup', 'bread', 'chicken', 'rice', 'pasta', 'pizza', 'sandwich', 'salad', 'cake', 'cookie', 'apple', 'orange', 'banana',
      'coffee', 'tea', 'milk', 'juice', 'water', 'broth', 'sauce', 'cheese', 'butter', 'egg', 'noodles', 'vegetables', 'carrots', 'celery',
      // Objects
      'chair', 'table', 'desk', 'door', 'window', 'floor', 'wall', 'ceiling', 'bed', 'lamp', 'phone', 'computer', 'book', 'pen', 'paper',
      'car', 'bus', 'bike', 'train', 'plane', 'boat', 'truck',
      'shirt', 'pants', 'shoes', 'hat', 'jacket', 'coat', 'dress', 'socks', 'sneakers',
      'bowl', 'plate', 'cup', 'glass', 'spoon', 'fork', 'knife', 'pot', 'pan',
      // Places
      'kitchen', 'bedroom', 'bathroom', 'living room', 'classroom', 'office', 'store', 'park', 'street', 'house', 'apartment', 'school',
      // Body parts
      'hand', 'hands', 'finger', 'fingers', 'arm', 'arms', 'leg', 'legs', 'foot', 'feet', 'head', 'face', 'eye', 'eyes', 'nose', 'mouth', 'ear', 'ears',
      // Nature
      'tree', 'flower', 'grass', 'leaf', 'leaves', 'rock', 'stone', 'river', 'lake', 'ocean', 'mountain', 'sky', 'sun', 'moon', 'star', 'cloud', 'rain', 'snow'
    ];

    // Specific adjectives (concrete descriptors)
    const concreteAdjectives = [
      'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'black', 'white', 'brown', 'gray', 'golden',
      'hot', 'cold', 'warm', 'cool', 'freezing', 'boiling', 'lukewarm',
      'loud', 'quiet', 'soft', 'hard', 'smooth', 'rough', 'sharp', 'dull',
      'sweet', 'sour', 'salty', 'bitter', 'spicy', 'savory', 'bland', 'rich', 'creamy',
      'tall', 'short', 'big', 'small', 'large', 'tiny', 'huge', 'massive', 'little',
      'old', 'new', 'young', 'ancient', 'modern', 'fresh', 'stale',
      'thick', 'thin', 'wide', 'narrow', 'long', 'round', 'square', 'flat',
      'wet', 'dry', 'damp', 'sticky', 'slippery', 'dusty', 'dirty', 'clean'
    ];

    // Vague words to avoid
    const vagueWords = [
      'thing', 'things', 'stuff', 'something', 'anything', 'everything', 'nothing',
      'nice', 'good', 'bad', 'great', 'awesome', 'cool', 'fine', 'okay',
      'very', 'really', 'pretty', 'quite', 'somewhat', 'kind of', 'sort of',
      'a lot', 'lots', 'many', 'some', 'few', 'several',
      'went', 'got', 'did', 'made', 'had'
    ];

    // Count concrete nouns
    let concreteNounCount = 0;
    for (const noun of concreteNouns) {
      if (lower.includes(noun)) {
        concreteNounCount++;
        if (d.concreteWords.length < 10) d.concreteWords.push(noun);
      }
    }

    // Count concrete adjectives
    let concreteAdjCount = 0;
    for (const adj of concreteAdjectives) {
      const regex = new RegExp(`\\b${adj}\\b`, 'gi');
      if (regex.test(lower)) {
        concreteAdjCount++;
        if (d.concreteWords.length < 10) d.concreteWords.push(adj);
      }
    }

    // Count vague words
    let vagueCount = 0;
    for (const vague of vagueWords) {
      if (lower.includes(vague)) {
        vagueCount++;
        d.vagueWords.push(vague);
      }
    }

    // Score based on concrete words
    const totalConcrete = concreteNounCount + concreteAdjCount;
    if (totalConcrete >= 15) { s += 0.4; d.indicators.push('Excellent use of concrete words'); }
    else if (totalConcrete >= 10) { s += 0.3; d.indicators.push('Strong concrete language'); }
    else if (totalConcrete >= 6) { s += 0.2; d.indicators.push('Good concrete words'); }
    else if (totalConcrete >= 3) { s += 0.1; d.indicators.push('Some concrete words'); }

    // Bonus for specific details (numbers, proper nouns)
    const hasNumbers = /\b\d+\b/.test(text);
    const hasProperNouns = /\b[A-Z][a-z]+\b/.test(text);
    if (hasNumbers) { s += 0.1; d.indicators.push('Specific numbers/quantities'); }
    if (hasProperNouns) { s += 0.05; }

    // Penalty for too many vague words
    if (vagueCount >= 8) { s -= 0.15; d.indicators.push('Reduce vague language'); }
    else if (vagueCount >= 5) { s -= 0.08; }

    s = Math.max(0.1, Math.min(1, s));
    const feedback = s >= 0.75 ? 'Excellent concrete language!' : s >= 0.55 ? 'Good use of specific words.' : s >= 0.35 ? 'Add more concrete details.' : 'Use more specific, concrete words.';
    return { rawScore: s, feedback, details: d };
  }

  // Score word precision (specific vs vague words)
  _scoreWordPrecision(text) {
    let s = 0.2;
    const d = { indicators: [], preciseWords: [], vagueWords: [] };
    const lower = text.toLowerCase();

    // Precise/specific verbs (vs vague verbs like "went", "got", "made")
    const preciseVerbs = [
      'crept', 'shuffled', 'trudged', 'sprinted', 'dashed', 'strolled', 'wandered', 'marched', 'stumbled', 'tiptoed',
      'glared', 'squinted', 'peered', 'gazed', 'stared', 'glimpsed', 'spotted', 'observed', 'examined', 'scrutinized',
      'whispered', 'murmured', 'muttered', 'stammered', 'shouted', 'bellowed', 'shrieked', 'exclaimed', 'announced', 'declared',
      'grabbed', 'clutched', 'snatched', 'gripped', 'seized', 'grasped', 'clasped', 'clenched',
      'devoured', 'nibbled', 'savored', 'gulped', 'sipped', 'munched',
      'shattered', 'crumbled', 'collapsed', 'exploded', 'erupted', 'burst',
      'trembled', 'shivered', 'quivered', 'shuddered', 'flinched', 'recoiled',
      'vanished', 'disappeared', 'emerged', 'materialized', 'surfaced',
      'rippling', 'echoed', 'studying', 'prickle', 'drumming', 'rustled'
    ];

    // Precise nouns (specific vs generic)
    const preciseNouns = [
      'fox', 'meadow', 'snout', 'thicket', 'bushes', 'canyon', 'telescope', 'ingredients',
      'oak', 'maple', 'pine', 'willow', 'cedar', 'birch',
      'sparrow', 'hawk', 'eagle', 'robin', 'crow', 'owl',
      'terrier', 'labrador', 'poodle', 'beagle', 'collie',
      'daffodil', 'tulip', 'rose', 'daisy', 'sunflower', 'lily',
      'granite', 'marble', 'limestone', 'sandstone', 'slate',
      'velvet', 'silk', 'cotton', 'linen', 'wool', 'denim', 'leather',
      'cinnamon', 'vanilla', 'rosemary', 'thyme', 'garlic', 'ginger',
      'amber', 'rusty', 'silver', 'moonlit', 'crimson', 'azure', 'emerald'
    ];

    // Vague words to detect
    const vagueWords = [
      'thing', 'things', 'stuff', 'something', 'place', 'area',
      'went', 'got', 'made', 'did', 'put', 'came',
      'said', 'looked', 'walked', 'moved',
      'nice', 'good', 'bad', 'big', 'small', 'pretty',
      'very', 'really', 'quite', 'somewhat', 'kind of', 'sort of'
    ];

    // Count precise verbs
    let preciseVerbCount = 0;
    for (const verb of preciseVerbs) {
      if (lower.includes(verb)) {
        preciseVerbCount++;
        if (d.preciseWords.length < 10) d.preciseWords.push(verb);
      }
    }

    // Count precise nouns
    let preciseNounCount = 0;
    for (const noun of preciseNouns) {
      if (lower.includes(noun)) {
        preciseNounCount++;
        if (d.preciseWords.length < 10) d.preciseWords.push(noun);
      }
    }

    // Count vague words
    let vagueCount = 0;
    for (const vague of vagueWords) {
      const regex = new RegExp(`\\b${vague}\\b`, 'gi');
      const matches = lower.match(regex) || [];
      vagueCount += matches.length;
      if (matches.length > 0) d.vagueWords.push(vague);
    }

    // Score
    const totalPrecise = preciseVerbCount + preciseNounCount;
    if (totalPrecise >= 12) { s += 0.45; d.indicators.push('Excellent word precision'); }
    else if (totalPrecise >= 8) { s += 0.35; d.indicators.push('Strong precise language'); }
    else if (totalPrecise >= 5) { s += 0.25; d.indicators.push('Good word choices'); }
    else if (totalPrecise >= 3) { s += 0.15; d.indicators.push('Some precise words'); }
    else if (totalPrecise >= 1) { s += 0.08; d.indicators.push('Few precise words'); }

    // Bonus for avoiding vague words
    if (vagueCount === 0) { s += 0.2; d.indicators.push('No vague words!'); }
    else if (vagueCount <= 2) { s += 0.1; d.indicators.push('Minimal vague words'); }
    else if (vagueCount >= 6) { s -= 0.15; d.indicators.push('Too many vague words'); }
    else if (vagueCount >= 4) { s -= 0.08; }

    s = Math.max(0.1, Math.min(1, s));
    const feedback = s >= 0.75 ? 'Excellent word precision!' : s >= 0.55 ? 'Good precise language.' : s >= 0.35 ? 'Use more precise words.' : 'Replace vague words with specific ones.';
    return { rawScore: s, feedback, details: d };
  }

  // Score use of vivid verbs
  _scoreVividVerbs(text) {
    let s = 0.2;
    const d = { indicators: [], vividVerbs: [], weakVerbs: [] };
    const lower = text.toLowerCase();

    // Vivid/strong verbs
    const vividVerbs = [
      // Movement
      'crept', 'shuffled', 'trudged', 'sprinted', 'dashed', 'strolled', 'wandered', 'marched', 'stumbled', 'tiptoed', 'charged', 'bolted', 'scurried', 'lunged', 'leaped', 'soared', 'plunged', 'swooped',
      // Looking
      'glared', 'squinted', 'peered', 'gazed', 'stared', 'glimpsed', 'spotted', 'observed', 'examined', 'scrutinized', 'scanned', 'surveyed',
      // Speaking
      'whispered', 'murmured', 'muttered', 'stammered', 'shouted', 'bellowed', 'shrieked', 'exclaimed', 'announced', 'declared', 'hissed', 'growled', 'barked', 'snapped', 'pleaded', 'demanded',
      // Grabbing/touching
      'grabbed', 'clutched', 'snatched', 'gripped', 'seized', 'grasped', 'clasped', 'clenched', 'yanked', 'tugged', 'caressed', 'stroked',
      // Breaking/destroying
      'shattered', 'crumbled', 'collapsed', 'exploded', 'erupted', 'burst', 'smashed', 'crushed', 'demolished', 'obliterated',
      // Body reactions
      'trembled', 'shivered', 'quivered', 'shuddered', 'flinched', 'recoiled', 'froze', 'tensed', 'relaxed',
      // Appearing/disappearing
      'vanished', 'disappeared', 'emerged', 'materialized', 'surfaced', 'faded', 'dissolved',
      // Eating
      'devoured', 'nibbled', 'savored', 'gulped', 'sipped', 'munched', 'gnawed',
      // Sounds
      'echoed', 'thundered', 'roared', 'rumbled', 'rattled', 'clanged', 'whistled', 'hummed',
      // Other vivid
      'rippling', 'studying', 'prickle', 'drumming', 'rustled', 'flickered', 'glimmered', 'glistened', 'sparkled', 'blazed', 'melted', 'oozed'
    ];

    // Weak/generic verbs
    const weakVerbs = ['went', 'got', 'made', 'did', 'put', 'came', 'said', 'looked', 'walked', 'moved', 'ran', 'saw', 'had', 'was', 'were', 'is', 'are', 'be', 'being', 'been'];

    // Count vivid verbs
    let vividCount = 0;
    for (const verb of vividVerbs) {
      if (lower.includes(verb)) {
        vividCount++;
        if (d.vividVerbs.length < 10) d.vividVerbs.push(verb);
      }
    }

    // Count weak verbs (only as negatives if overused)
    let weakCount = 0;
    for (const verb of weakVerbs) {
      const regex = new RegExp(`\\b${verb}\\b`, 'gi');
      const matches = lower.match(regex) || [];
      weakCount += matches.length;
    }

    // Score based on vivid verbs
    if (vividCount >= 10) { s += 0.5; d.indicators.push('Excellent vivid verbs!'); }
    else if (vividCount >= 7) { s += 0.4; d.indicators.push('Many vivid verbs'); }
    else if (vividCount >= 5) { s += 0.3; d.indicators.push('Good vivid language'); }
    else if (vividCount >= 3) { s += 0.2; d.indicators.push('Some vivid verbs'); }
    else if (vividCount >= 1) { s += 0.1; d.indicators.push('Few vivid verbs'); }

    // Ratio bonus (vivid vs weak)
    const words = text.split(/\s+/).length;
    const vividRatio = vividCount / Math.max(words / 20, 1); // Expect ~1 vivid per 20 words
    if (vividRatio >= 1) { s += 0.15; d.indicators.push('High vivid verb density'); }

    // Small penalty only if almost no vivid verbs and many weak ones
    if (vividCount < 2 && weakCount > 10) { s -= 0.1; }

    s = Math.max(0.1, Math.min(1, s));
    const feedback = s >= 0.75 ? 'Excellent vivid verbs!' : s >= 0.55 ? 'Good use of vivid verbs.' : s >= 0.35 ? 'Add more vivid verbs.' : 'Replace weak verbs with vivid ones.';
    return { rawScore: s, feedback, details: d };
  }

  // Score how well the writing creates reader experience/immersion
  _scoreReaderExperience(text) {
    let s = 0.2;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // 1. Sensory immersion (can reader see/hear/feel it?)
    let sensoryScore = 0;
    for (const [sense, patterns] of Object.entries(NARRATIVE_PATTERNS.sensory)) {
      for (const p of patterns) {
        if (p.test(text)) { sensoryScore++; break; }
      }
    }
    if (sensoryScore >= 4) { s += 0.2; d.indicators.push('Full sensory immersion'); }
    else if (sensoryScore >= 3) { s += 0.15; d.indicators.push('Good sensory experience'); }
    else if (sensoryScore >= 2) { s += 0.1; d.indicators.push('Some sensory detail'); }

    // 2. Emotional engagement (does reader feel something?)
    const emotionalWords = ['felt', 'feeling', 'heart', 'suddenly', 'finally', 'at last', 'couldn\'t believe', 'amazed', 'terrified', 'thrilled', 'relieved', 'devastated', 'overjoyed'];
    let emotionCount = 0;
    for (const word of emotionalWords) if (lower.includes(word)) emotionCount++;
    if (emotionCount >= 4) { s += 0.15; d.indicators.push('Strong emotional engagement'); }
    else if (emotionCount >= 2) { s += 0.1; d.indicators.push('Emotional connection'); }

    // 3. Vivid verbs (action that reader can visualize)
    const vividVerbs = ['slammed', 'crashed', 'exploded', 'whispered', 'screamed', 'crept', 'soared', 'plunged', 'trembled', 'shattered', 'melted', 'burst', 'raced', 'crawled', 'leaped', 'grabbed', 'clutched', 'yanked'];
    let vividCount = 0;
    for (const verb of vividVerbs) if (lower.includes(verb)) vividCount++;
    if (vividCount >= 4) { s += 0.15; d.indicators.push('Vivid action words'); }
    else if (vividCount >= 2) { s += 0.1; d.indicators.push('Some vivid language'); }

    // 4. Show don't tell (specific details vs summaries)
    const showPhrases = ['i watched', 'i saw', 'i heard', 'i felt', 'i could see', 'i could hear', 'i could feel', 'i could smell', 'i could taste', 'my eyes', 'my ears', 'my hands', 'my fingers'];
    let showCount = 0;
    for (const phrase of showPhrases) if (lower.includes(phrase)) showCount++;
    if (showCount >= 3) { s += 0.15; d.indicators.push('Shows rather than tells'); }
    else if (showCount >= 1) { s += 0.08; d.indicators.push('Some showing'); }

    // 5. Present-moment feel (reader feels "in" the scene)
    const presentMoment = ['suddenly', 'right then', 'at that moment', 'just then', 'in that instant', 'everything', 'the moment', 'as i', 'when i'];
    let presentCount = 0;
    for (const phrase of presentMoment) if (lower.includes(phrase)) presentCount++;
    if (presentCount >= 2) { s += 0.1; d.indicators.push('Creates immediacy'); }

    // 6. Specific details over generalizations
    const hasSpecifics = /\b(favorite|first|last|only|every|each)\b/.test(lower) && /\b(my|the|this)\b/.test(lower);
    if (hasSpecifics) { s += 0.05; d.indicators.push('Personal/specific details'); }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent reader immersion!' : s >= 0.55 ? 'Good reader experience.' : s >= 0.35 ? 'Engages reader somewhat.' : 'Help reader experience the scene more vividly.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSuspense(text) {
    let s = 0.2;
    const d = { indicators: [] };

    // Check suspense patterns
    let suspenseCount = 0;
    for (const p of NARRATIVE_PATTERNS.suspense) {
      const matches = text.match(p) || [];
      suspenseCount += matches.length;
    }
    if (suspenseCount >= 5) { s += 0.35; d.indicators.push('Strong tension building'); }
    else if (suspenseCount >= 3) { s += 0.25; d.indicators.push('Good suspense'); }
    else if (suspenseCount >= 1) { s += 0.15; d.indicators.push('Some tension'); }

    // Check for questions (create uncertainty)
    const questions = (text.match(/\?/g) || []).length;
    if (questions >= 2) { s += 0.1; d.indicators.push('Questions build intrigue'); }

    // Check for short sentences (tension technique)
    const shortSentences = (text.match(/[^.!?]{5,25}[.!?]/g) || []).length;
    if (shortSentences >= 3) { s += 0.15; d.indicators.push('Pacing creates tension'); }

    // Emotional reactions heighten suspense
    let emotionCount = 0;
    for (const p of NARRATIVE_PATTERNS.emotions) emotionCount += (text.match(p) || []).length;
    if (emotionCount >= 3) { s += 0.1; d.indicators.push('Emotional tension'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent suspense!' : s >= 0.5 ? 'Good tension building.' : s >= 0.3 ? 'Some suspense present.' : 'Build more tension.';
    return { rawScore: s, feedback, details: d };
  }

  _scorePacing(text) {
    let s = 0.25;
    const d = { indicators: [], shortSentences: 0, veryShortSentences: 0, longSentences: 0, fragments: 0 };

    const sentences = this._getSentences(text);
    
    for (const sent of sentences) {
      const wordCount = sent.split(/\s+/).filter(w => w.length > 0).length;
      if (wordCount <= 3) {
        d.veryShortSentences++;  // "Silence.", "Cold.", "Empty."
        d.shortSentences++;
      } else if (wordCount <= 8) {
        d.shortSentences++;
      } else if (wordCount >= 20) {
        d.longSentences++;
      }
    }

    // Count intentional fragments (single words or very short phrases ending in period)
    const fragments = text.match(/\.\s+[A-Z][a-z]*\./g) || [];
    d.fragments = fragments.length;

    // SLOW PACING: Many short sentences = building tension
    if (d.veryShortSentences >= 5) {
      s += 0.35;
      d.indicators.push('Excellent slow pacing with punchy sentences');
    } else if (d.veryShortSentences >= 3) {
      s += 0.25;
      d.indicators.push('Good use of short sentences for tension');
    } else if (d.veryShortSentences >= 1) {
      s += 0.15;
      d.indicators.push('Some short sentences');
    }

    // Variety in sentence length = good pacing control
    if (d.shortSentences >= 5 && d.longSentences >= 1) {
      s += 0.25;
      d.indicators.push('Excellent sentence variety');
    } else if (d.shortSentences >= 3 && d.longSentences >= 1) {
      s += 0.2;
      d.indicators.push('Good sentence rhythm');
    } else if (d.shortSentences >= 2 || d.longSentences >= 1) {
      s += 0.1;
      d.indicators.push('Some variety');
    }

    // Check for dramatic pauses / fragments
    const dramaticPatterns = (text.match(/\.\s+(And|But|Then|Nothing|Silence|Darkness|Wait|Stop|Listen|Gone|Dead|Empty|Cold|Still)\./gi) || []).length;
    if (dramaticPatterns >= 2) {
      s += 0.15;
      d.indicators.push('Dramatic fragments');
    } else if (dramaticPatterns >= 1) {
      s += 0.08;
      d.indicators.push('Dramatic pause');
    }

    // Check for suspense pacing patterns
    const suspensePatterns = [
      /She (waited|listened|watched|froze)/i,
      /He (waited|listened|watched|froze)/i,
      /(couldn't|could not) (move|breathe|speak)/i,
      /heart (stopped|pounded|raced|beat)/i,
      /time (stopped|froze|slowed)/i
    ];
    let suspenseCount = 0;
    for (const p of suspensePatterns) if (p.test(text)) suspenseCount++;
    if (suspenseCount >= 2) {
      s += 0.15;
      d.indicators.push('Suspense pacing');
    } else if (suspenseCount >= 1) {
      s += 0.08;
      d.indicators.push('Some suspense');
    }

    // Check pacing words
    let slowCount = 0, fastCount = 0;
    for (const p of NARRATIVE_PATTERNS.pacing.slow) slowCount += (text.match(p) || []).length;
    for (const p of NARRATIVE_PATTERNS.pacing.fast) fastCount += (text.match(p) || []).length;

    if (slowCount >= 2) {
      s += 0.1;
      d.indicators.push('Slow pacing words');
    }
    if (fastCount >= 2) {
      s += 0.1;
      d.indicators.push('Fast pacing words');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent pacing control!' : s >= 0.55 ? 'Good pacing.' : s >= 0.35 ? 'Some pacing control.' : 'Vary your pacing.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreCharacter(text) {
    let s = 0.25;
    const d = { indicators: [] };

    // Check for names/pronouns (characters present)
    const names = text.match(/\b[A-Z][a-z]+\b/g) || [];
    const pronouns = (text.match(/\b(he|she|they|him|her|them|his|hers|their)\b/gi) || []).length;
    if (names.length >= 2 || pronouns >= 3) { s += 0.15; d.indicators.push('Characters present'); }

    // Check for character actions
    let actionCount = 0;
    for (const p of NARRATIVE_PATTERNS.actions) actionCount += (text.match(p) || []).length;
    if (actionCount >= 3) { s += 0.2; d.indicators.push('Character actions'); }

    // Check for character emotions/reactions
    let emotionCount = 0;
    for (const p of NARRATIVE_PATTERNS.emotions) emotionCount += (text.match(p) || []).length;
    if (emotionCount >= 3) { s += 0.25; d.indicators.push('Character emotions shown'); }
    else if (emotionCount >= 1) { s += 0.15; d.indicators.push('Some emotion'); }

    // Dialogue reveals character
    const dialogue = (text.match(/"[^"]+"|"[^"]+"/g) || []).length;
    if (dialogue >= 1) { s += 0.1; d.indicators.push('Dialogue reveals character'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong characterization!' : s >= 0.5 ? 'Good character development.' : s >= 0.3 ? 'Characters present.' : 'Develop your characters more.';
    return { rawScore: s, feedback, details: d };
  }

  // Score character introduction specifically (for story openings)
  _scoreCharacterIntroduction(text) {
    let s = 0.2;
    const d = { indicators: [], hasName: false, hasBackground: false, hasEmotion: false };
    const lower = text.toLowerCase();

    // 1. Check for character name (capitalized name)
    const names = text.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g) || [];
    // Filter out common non-name words
    const nonNames = ['the', 'and', 'but', 'she', 'her', 'his', 'they', 'this', 'that', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    const realNames = names.filter(n => !nonNames.includes(n.toLowerCase()) && n.length > 2);
    
    if (realNames.length >= 1) {
      s += 0.2;
      d.hasName = true;
      d.indicators.push('Character named');
    }

    // 2. Check for physical description
    const physicalDesc = /\b(tall|short|young|old|blonde|brunette|dark|light|skinny|heavy|thin|muscular|slim|petite|hair|eyes|face|wearing|wore|dressed)\b/i;
    if (physicalDesc.test(text)) {
      s += 0.1;
      d.indicators.push('Physical description');
    }

    // 3. Check for character background/history
    const backgroundPatterns = [
      /\b(used to|had been|was from|moved from|grew up|lived in|years ago|before|previously)\b/i,
      /\b(mom|dad|mother|father|parent|family|friend|sister|brother)\b/i,
      /\b(school|job|work|home|house|apartment)\b/i
    ];
    let bgCount = 0;
    for (const p of backgroundPatterns) if (p.test(text)) bgCount++;
    if (bgCount >= 2) {
      s += 0.2;
      d.hasBackground = true;
      d.indicators.push('Character background established');
    } else if (bgCount >= 1) {
      s += 0.1;
      d.indicators.push('Some background');
    }

    // 4. Check for character emotions/feelings
    const emotionWords = /\b(felt|feeling|nervous|anxious|excited|scared|worried|happy|sad|angry|confused|hopeful|dreading|afraid|terrified|eager|reluctant|stomach|heart|knots|butterflies)\b/i;
    if (emotionWords.test(text)) {
      s += 0.15;
      d.hasEmotion = true;
      d.indicators.push('Character emotions shown');
    }

    // 5. Check for character thoughts
    const thoughtPatterns = /\b(thought|wondered|knew|realized|hoped|wished|wanted|needed|remembered|forgot)\b/i;
    if (thoughtPatterns.test(text)) {
      s += 0.1;
      d.indicators.push('Character thoughts');
    }

    // 6. Check for character traits/personality
    const traitWords = /\b(shy|outgoing|quiet|loud|brave|timid|friendly|smart|clever|kind|mean|funny|serious|confident|nervous)\b/i;
    if (traitWords.test(text)) {
      s += 0.1;
      d.indicators.push('Character traits');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent character introduction!' : s >= 0.55 ? 'Good character introduction.' : s >= 0.35 ? 'Character introduced.' : 'Introduce your character more fully.';
    return { rawScore: s, feedback, details: d };
  }

  // Score situation/context establishment
  _scoreSituation(text) {
    let s = 0.2;
    const d = { indicators: [], hasProblem: false, hasContext: false };
    const lower = text.toLowerCase();

    // 1. Check for situation/problem indicators
    const problemPatterns = [
      /\b(first day|new (school|job|town|city|house)|just (moved|arrived|started))\b/i,
      /\b(had to|needed to|must|supposed to|required)\b/i,
      /\b(problem|challenge|difficult|trouble|issue|situation)\b/i,
      /\b(didn't know|wasn't sure|couldn't find|had no idea|unknown|unfamiliar|strange|foreign)\b/i
    ];
    let problemCount = 0;
    for (const p of problemPatterns) if (p.test(text)) problemCount++;
    
    if (problemCount >= 3) {
      s += 0.3;
      d.hasProblem = true;
      d.indicators.push('Clear situation established');
    } else if (problemCount >= 2) {
      s += 0.2;
      d.indicators.push('Situation present');
    } else if (problemCount >= 1) {
      s += 0.1;
      d.indicators.push('Some context');
    }

    // 2. Check for time/place context
    const contextPatterns = [
      /\b(morning|afternoon|evening|night|day|week|month|year)\b/i,
      /\b(today|yesterday|tomorrow|now|then|finally|suddenly)\b/i,
      /\b(here|there|inside|outside|at the|in the)\b/i
    ];
    let contextCount = 0;
    for (const p of contextPatterns) if (p.test(text)) contextCount++;
    
    if (contextCount >= 2) {
      s += 0.15;
      d.hasContext = true;
      d.indicators.push('Time/place context');
    }

    // 3. Check for stakes/why it matters
    const stakesPatterns = [
      /\b(important|crucial|critical|everything|nothing|only chance|last|first)\b/i,
      /\b(wanted|needed|hoped|wished|dreamed|feared)\b/i,
      /\b(everyone|no one|nobody|somebody|anybody)\b/i
    ];
    let stakesCount = 0;
    for (const p of stakesPatterns) if (p.test(text)) stakesCount++;
    
    if (stakesCount >= 2) {
      s += 0.15;
      d.indicators.push('Stakes established');
    } else if (stakesCount >= 1) {
      s += 0.08;
      d.indicators.push('Some stakes');
    }

    // 4. Check for "what's happening now" action
    const currentAction = /\b(stood|walked|sat|looked|watched|waited|entered|stepped|approached|arrived|reached)\b/i;
    if (currentAction.test(text)) {
      s += 0.1;
      d.indicators.push('Current action shown');
    }

    // 5. Check for character goal or need
    const goalPatterns = /\b(wanted to|needed to|had to|trying to|looking for|searching for|hoping to|going to)\b/i;
    if (goalPatterns.test(text)) {
      s += 0.1;
      d.indicators.push('Character goal present');
    }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent situation setup!' : s >= 0.55 ? 'Good situation established.' : s >= 0.35 ? 'Situation present.' : 'Clarify the situation more.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSetting(text) {
    let s = 0.25;
    const d = { indicators: [] };

    // Check for setting words
    let settingCount = 0;
    for (const p of NARRATIVE_PATTERNS.setting) settingCount += (text.match(p) || []).length;
    if (settingCount >= 6) { s += 0.35; d.indicators.push('Vivid setting'); }
    else if (settingCount >= 3) { s += 0.25; d.indicators.push('Setting established'); }
    else if (settingCount >= 1) { s += 0.15; d.indicators.push('Setting mentioned'); }

    // Check for atmosphere/mood words
    const moodWords = ['eerie', 'dark', 'bright', 'cold', 'warm', 'quiet', 'noisy', 'peaceful', 'tense', 'mysterious', 'creepy', 'cozy', 'lonely', 'crowded'];
    let moodCount = 0;
    const lower = text.toLowerCase();
    for (const word of moodWords) if (lower.includes(word)) moodCount++;
    if (moodCount >= 2) { s += 0.2; d.indicators.push('Atmosphere created'); }
    else if (moodCount >= 1) { s += 0.1; d.indicators.push('Some mood'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent setting/atmosphere!' : s >= 0.5 ? 'Good setting description.' : s >= 0.3 ? 'Setting present.' : 'Describe the setting more.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreFigurativeLanguage(text) {
    let s = 0.2;
    const d = { indicators: [], examples: [] };

    // Check for similes and metaphors
    for (const p of NARRATIVE_PATTERNS.figurative) {
      const matches = text.match(p) || [];
      if (matches.length > 0) {
        d.examples.push(...matches.slice(0, 2));
      }
    }

    if (d.examples.length >= 3) { s += 0.5; d.indicators.push('Rich figurative language'); }
    else if (d.examples.length >= 2) { s += 0.35; d.indicators.push('Good use of figurative language'); }
    else if (d.examples.length >= 1) { s += 0.25; d.indicators.push('Figurative language present'); }

    // Check for vivid verbs and adjectives
    let vividCount = 0;
    const lower = text.toLowerCase();
    for (const w of VIVID_VERBS) if (lower.includes(w)) vividCount++;
    for (const w of VIVID_ADJECTIVES) if (lower.includes(w)) vividCount++;
    if (vividCount >= 3) { s += 0.15; d.indicators.push('Vivid word choice'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent figurative language!' : s >= 0.5 ? 'Good imagery.' : s >= 0.3 ? 'Some figurative language.' : 'Add similes or metaphors.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSequence(text) {
    let s = 0.3;
    const d = { indicators: [] };

    // Check sequence words
    let seqCount = 0;
    for (const p of NARRATIVE_PATTERNS.sequence) seqCount += (text.match(p) || []).length;
    if (seqCount >= 4) { s += 0.35; d.indicators.push('Clear event sequence'); }
    else if (seqCount >= 2) { s += 0.25; d.indicators.push('Sequence present'); }
    else if (seqCount >= 1) { s += 0.15; d.indicators.push('Some sequence'); }

    // Check for logical flow (multiple sentences)
    const sentences = this._getSentences(text);
    if (sentences.length >= 4) { s += 0.15; d.indicators.push('Multiple events'); }
    else if (sentences.length >= 2) { s += 0.1; d.indicators.push('Story continues'); }

    // Check for action progression
    let actionCount = 0;
    for (const p of NARRATIVE_PATTERNS.actions) actionCount += (text.match(p) || []).length;
    if (actionCount >= 3) { s += 0.1; d.indicators.push('Actions progress'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent sequence!' : s >= 0.5 ? 'Good event order.' : s >= 0.3 ? 'Some sequence.' : 'Clarify the order of events.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreResolution(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Check for resolution/ending words
    const resolutionWords = ['finally', 'at last', 'in the end', 'eventually', 'realized', 'understood', 'knew', 'discovered', 'found', 'learned', 'decided', 'solved', 'resolved', 'peace', 'safe', 'home', 'smiled', 'happy', 'relieved'];
    let resCount = 0;
    for (const w of resolutionWords) if (lower.includes(w)) resCount++;
    if (resCount >= 3) { s += 0.35; d.indicators.push('Clear resolution'); }
    else if (resCount >= 1) { s += 0.2; d.indicators.push('Resolution present'); }

    // Check if story concludes (last sentence quality)
    const sentences = this._getSentences(text);
    if (sentences.length >= 2) {
      const lastSent = sentences[sentences.length - 1].toLowerCase();
      if (/\b(finally|now|knew|realized|would never|always|smiled|safe)\b/.test(lastSent)) {
        s += 0.2;
        d.indicators.push('Satisfying ending');
      }
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong resolution!' : s >= 0.5 ? 'Good ending.' : s >= 0.3 ? 'Some closure.' : 'Add a clearer ending.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreCharacterGrowth(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Words indicating change/growth
    const growthWords = ['realized', 'understood', 'learned', 'changed', 'decided', 'knew', 'felt different', 'never forget', 'would always', 'from now on', 'finally', 'brave', 'courage', 'strength', 'overcome', 'faced', 'accepted'];
    let growthCount = 0;
    for (const w of growthWords) if (lower.includes(w)) growthCount++;
    if (growthCount >= 3) { s += 0.4; d.indicators.push('Clear character growth'); }
    else if (growthCount >= 1) { s += 0.25; d.indicators.push('Growth suggested'); }

    // Emotional journey
    let emotionCount = 0;
    for (const p of NARRATIVE_PATTERNS.emotions) emotionCount += (text.match(p) || []).length;
    if (emotionCount >= 3) { s += 0.2; d.indicators.push('Emotional journey'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent character growth!' : s >= 0.5 ? 'Good character development.' : s >= 0.3 ? 'Some growth shown.' : 'Show how the character changes.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSatisfaction(text, analysis) {
    let s = 0.3;
    const d = { indicators: [] };

    // Multiple story elements present
    const sentences = this._getSentences(text);
    if (sentences.length >= 3) { s += 0.15; d.indicators.push('Complete narrative'); }

    // Has dialogue
    const dialogue = (text.match(/"[^"]+"|"[^"]+"/g) || []).length;
    if (dialogue >= 1) { s += 0.15; d.indicators.push('Includes dialogue'); }

    // Has description
    let sensoryCount = 0;
    for (const patterns of Object.values(NARRATIVE_PATTERNS.sensory)) {
      for (const p of patterns) sensoryCount += (text.match(p) || []).length;
    }
    if (sensoryCount >= 3) { s += 0.15; d.indicators.push('Descriptive'); }

    // Has emotional content
    let emotionCount = 0;
    for (const p of NARRATIVE_PATTERNS.emotions) emotionCount += (text.match(p) || []).length;
    if (emotionCount >= 2) { s += 0.15; d.indicators.push('Emotionally engaging'); }

    // Good length
    const words = text.match(/\b\w+\b/g) || [];
    if (words.length >= 80) { s += 0.1; d.indicators.push('Well-developed'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Satisfying and complete!' : s >= 0.5 ? 'Good overall.' : s >= 0.3 ? 'Mostly complete.' : 'Develop your story more.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreEventDevelopment(text) {
    let s = 0.3;
    const d = { indicators: [] };

    // Check for action verbs
    let actionCount = 0;
    for (const p of NARRATIVE_PATTERNS.actions) actionCount += (text.match(p) || []).length;
    if (actionCount >= 5) { s += 0.35; d.indicators.push('Rich action sequence'); }
    else if (actionCount >= 3) { s += 0.25; d.indicators.push('Good action'); }
    else if (actionCount >= 1) { s += 0.15; d.indicators.push('Actions present'); }

    // Check for sequence/progression
    let seqCount = 0;
    for (const p of NARRATIVE_PATTERNS.sequence) seqCount += (text.match(p) || []).length;
    if (seqCount >= 2) { s += 0.15; d.indicators.push('Events progress'); }

    // Multiple sentences = multiple events
    const sentences = this._getSentences(text);
    if (sentences.length >= 4) { s += 0.1; d.indicators.push('Multiple events'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent event development!' : s >= 0.5 ? 'Good events.' : s >= 0.3 ? 'Events present.' : 'Develop more events.';
    return { rawScore: s, feedback, details: d };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLIMAX / INTENSITY SCORING METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  _scoreIntensity(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Intense action words
    const intenseActions = ['exploded', 'crashed', 'screamed', 'shouted', 'raced', 'rushed', 'burst', 'slammed', 'pounded', 'roared', 'thundered', 'shattered', 'erupted', 'plunged', 'lunged', 'sprinted', 'dove', 'leaped', 'grabbed', 'yanked', 'thrust', 'hurled'];
    let intenseCount = 0;
    for (const word of intenseActions) if (lower.includes(word)) intenseCount++;
    if (intenseCount >= 4) { s += 0.3; d.indicators.push('Highly intense action'); }
    else if (intenseCount >= 2) { s += 0.2; d.indicators.push('Intense moments'); }
    else if (intenseCount >= 1) { s += 0.1; d.indicators.push('Some intensity'); }

    // Urgent/intense words
    const urgentWords = ['suddenly', 'immediately', 'instantly', 'without warning', 'chaos', 'panic', 'desperate', 'frantically', 'urgently', 'quickly', 'fast', 'hurry', 'now', 'must', 'had to'];
    let urgentCount = 0;
    for (const word of urgentWords) if (lower.includes(word)) urgentCount++;
    if (urgentCount >= 3) { s += 0.2; d.indicators.push('Urgent pacing'); }
    else if (urgentCount >= 1) { s += 0.1; d.indicators.push('Some urgency'); }

    // Exclamation marks (intense dialogue/narration)
    const exclamations = (text.match(/!/g) || []).length;
    if (exclamations >= 3) { s += 0.15; d.indicators.push('Exclamatory intensity'); }
    else if (exclamations >= 1) { s += 0.08; d.indicators.push('Exclamation used'); }

    // Short, punchy sentences (tension technique)
    const sentences = this._getSentences(text);
    const shortSentences = sentences.filter(s => s.split(/\s+/).length <= 6).length;
    if (shortSentences >= 3) { s += 0.1; d.indicators.push('Punchy pacing'); }

    // Sensory overload (multiple senses in intense moment)
    let sensoryCount = 0;
    for (const patterns of Object.values(NARRATIVE_PATTERNS.sensory)) {
      for (const p of patterns) if (p.test(text)) { sensoryCount++; break; }
    }
    if (sensoryCount >= 3) { s += 0.1; d.indicators.push('Sensory intensity'); }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent intensity!' : s >= 0.55 ? 'Good intensity.' : s >= 0.35 ? 'Some intensity present.' : 'Build more intensity.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreCharacterAction(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Action verbs (character doing things) - expanded list
    const actionVerbs = [
      'ran', 'jumped', 'leaped', 'grabbed', 'pushed', 'pulled', 'threw', 'caught', 'kicked', 'hit', 'punched',
      'walked', 'stepped', 'climbed', 'crawled', 'sprinted', 'dashed', 'rushed', 'charged', 'stumbled', 'fell',
      'stood', 'sat', 'knelt', 'crouched', 'ducked', 'dodged', 'turned', 'spun', 'twisted',
      'opened', 'closed', 'slammed', 'locked', 'broke', 'smashed', 'shattered',
      'reached', 'touched', 'held', 'gripped', 'clutched', 'released', 'dropped',
      'shouted', 'screamed', 'yelled', 'whispered', 'called', 'cried',
      'looked', 'saw', 'watched', 'stared', 'glanced', 'searched', 'found',
      'heard', 'listened', 'felt', 'sensed', 'noticed',
      'moved', 'lifted', 'carried', 'dragged', 'shoved'
    ];
    
    let actionCount = 0;
    for (const verb of actionVerbs) {
      const regex = new RegExp(`\\b${verb}(s|ed|ing)?\\b`, 'gi');
      actionCount += (text.match(regex) || []).length;
    }
    
    // Also check NARRATIVE_PATTERNS if available
    if (NARRATIVE_PATTERNS.actions) {
      for (const p of NARRATIVE_PATTERNS.actions) actionCount += (text.match(p) || []).length;
    }
    
    // More generous thresholds
    if (actionCount >= 12) { s += 0.4; d.indicators.push('Action-packed!'); }
    else if (actionCount >= 8) { s += 0.35; d.indicators.push('Many character actions'); }
    else if (actionCount >= 5) { s += 0.25; d.indicators.push('Strong action'); }
    else if (actionCount >= 3) { s += 0.15; d.indicators.push('Actions present'); }
    else if (actionCount >= 1) { s += 0.08; d.indicators.push('Some action'); }

    // Decision-making words (character choosing) - expanded
    const decisionWords = ['decided', 'chose', 'choice', 'had to', 'must', 'would', 'determined', 'resolved', 'made up', 'knew what', 'realized', 'before he could think', 'without thinking', 'instinct', 'legs moved', 'feet carried', 'couldn\'t stop'];
    let decisionCount = 0;
    for (const word of decisionWords) if (lower.includes(word)) decisionCount++;
    if (decisionCount >= 2) { s += 0.2; d.indicators.push('Character makes decisions'); }
    else if (decisionCount >= 1) { s += 0.1; d.indicators.push('Decision moment'); }

    // Physical responses - expanded
    const physicalWords = ['gripped', 'grabbed', 'reached', 'stepped', 'turned', 'ran', 'jumped', 'leaped', 'stood', 'sat', 'walked', 'moved', 'pushed', 'pulled', 'held', 'took', 'slammed', 'shoved', 'sprinted', 'rushed', 'charged', 'burst', 'broke'];
    let physicalCount = 0;
    for (const word of physicalWords) if (lower.includes(word)) physicalCount++;
    if (physicalCount >= 6) { s += 0.2; d.indicators.push('Strong physical action'); }
    else if (physicalCount >= 4) { s += 0.15; d.indicators.push('Physical actions'); }
    else if (physicalCount >= 2) { s += 0.08; d.indicators.push('Some physical action'); }

    // Dialogue as action
    const dialogueActions = (text.match(/"[^"]*"|"[^"]*"/g) || []).length;
    if (dialogueActions >= 2) { s += 0.1; d.indicators.push('Character speaks/acts'); }
    else if (dialogueActions >= 1) { s += 0.05; d.indicators.push('Dialogue present'); }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent character action!' : s >= 0.55 ? 'Good character action.' : s >= 0.35 ? 'Some action shown.' : 'Show the character doing more.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreEmotion(text) {
    let s = 0.2;
    const d = { indicators: [], emotionsFound: [] };
    const lower = text.toLowerCase();

    // Direct emotion words - expanded
    const emotions = {
      fear: ['scared', 'afraid', 'terrified', 'frightened', 'panic', 'panicked', 'anxious', 'nervous', 'worried', 'dread', 'horror', 'fear', 'terrifying', 'petrified'],
      anger: ['angry', 'furious', 'rage', 'mad', 'frustrated', 'annoyed', 'irritated', 'outraged', 'fury'],
      joy: ['happy', 'excited', 'thrilled', 'delighted', 'overjoyed', 'elated', 'relieved', 'proud', 'joy', 'relief'],
      sadness: ['sad', 'upset', 'devastated', 'heartbroken', 'disappointed', 'depressed', 'miserable', 'grief', 'crying', 'sobbing'],
      surprise: ['shocked', 'surprised', 'stunned', 'amazed', 'astonished', 'startled', 'bewildered'],
      courage: ['brave', 'courageous', 'determined', 'resolved', 'bold']
    };

    let emotionCount = 0;
    for (const [type, words] of Object.entries(emotions)) {
      for (const word of words) {
        if (lower.includes(word)) {
          emotionCount++;
          if (!d.emotionsFound.includes(type)) d.emotionsFound.push(type);
        }
      }
    }

    if (emotionCount >= 5) { s += 0.35; d.indicators.push('Rich emotional content'); }
    else if (emotionCount >= 3) { s += 0.25; d.indicators.push('Strong emotions'); }
    else if (emotionCount >= 1) { s += 0.15; d.indicators.push('Emotions present'); }

    // Physical manifestations of emotion - expanded
    const physicalEmotions = [
      'heart pounded', 'heart raced', 'heart beat', 'heart slammed', 'heart hammered', 'heart stopped', 'heart sank',
      'trembled', 'shook', 'shaking', 'trembling',
      'tears', 'crying', 'sobbing', 'wept',
      'laughed', 'smiled', 'grinned', 'frowned', 'scowled',
      'gasped', 'breath caught', 'couldn\'t breathe', 'held his breath', 'held her breath',
      'stomach churned', 'stomach dropped', 'knees weak', 'legs shook',
      'froze', 'frozen', 'went cold', 'blood ran cold',
      'sweating', 'sweaty', 'sweat', 'blushed', 'pale', 'white as',
      'screamed', 'shrieked', 'every instinct'
    ];
    let physicalCount = 0;
    for (const phrase of physicalEmotions) if (lower.includes(phrase)) physicalCount++;
    if (physicalCount >= 4) { s += 0.3; d.indicators.push('Vivid physical emotion'); }
    else if (physicalCount >= 2) { s += 0.2; d.indicators.push('Shows emotion physically'); }
    else if (physicalCount >= 1) { s += 0.1; d.indicators.push('Physical emotion cues'); }

    // Emotional dialogue (exclamations, questions)
    const emotionalDialogue = (text.match(/"[^"]*[!?][^"]*"|"[^"]*[!?][^"]*"/g) || []).length;
    if (emotionalDialogue >= 2) { s += 0.1; d.indicators.push('Emotional dialogue'); }
    else if (emotionalDialogue >= 1) { s += 0.05; d.indicators.push('Some emotional speech'); }

    // Internal thoughts showing emotion
    const internalThoughts = ['his brain screamed', 'her brain screamed', 'mind raced', 'thoughts', 'couldn\'t believe', 'couldn\'t think'];
    let thoughtCount = 0;
    for (const thought of internalThoughts) if (lower.includes(thought)) thoughtCount++;
    if (thoughtCount >= 1) { s += 0.1; d.indicators.push('Internal emotional state'); }

    // Variety of emotions
    if (d.emotionsFound.length >= 3) { s += 0.1; d.indicators.push('Emotional range'); }
    else if (d.emotionsFound.length >= 2) { s += 0.05; d.indicators.push('Multiple emotions'); }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent emotional depth!' : s >= 0.55 ? 'Good emotional content.' : s >= 0.35 ? 'Some emotion shown.' : 'Show more character emotions.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreConflict(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Conflict words
    const conflictWords = ['problem', 'trouble', 'difficult', 'challenge', 'struggle', 'fight', 'against', 'obstacle', 'barrier', 'enemy', 'opponent', 'danger', 'threat', 'risk', 'crisis', 'dilemma', 'conflict', 'tension', 'clash'];
    let conflictCount = 0;
    for (const word of conflictWords) if (lower.includes(word)) conflictCount++;
    if (conflictCount >= 3) { s += 0.3; d.indicators.push('Clear conflict'); }
    else if (conflictCount >= 1) { s += 0.15; d.indicators.push('Conflict present'); }

    // Internal conflict
    const internalWords = ['wanted to', 'had to', 'couldn\'t', 'should', 'torn', 'conflicted', 'unsure', 'hesitated', 'debated', 'wrestled', 'choice', 'decide'];
    let internalCount = 0;
    for (const word of internalWords) if (lower.includes(word)) internalCount++;
    if (internalCount >= 2) { s += 0.2; d.indicators.push('Internal conflict'); }
    else if (internalCount >= 1) { s += 0.1; d.indicators.push('Some internal struggle'); }

    // Opposition/obstacles
    const oppositionWords = ['but', 'however', 'yet', 'although', 'despite', 'instead', 'unfortunately', 'blocked', 'stopped', 'prevented', 'couldn\'t'];
    let oppositionCount = 0;
    for (const word of oppositionWords) if (lower.includes(word)) oppositionCount++;
    if (oppositionCount >= 3) { s += 0.15; d.indicators.push('Obstacles shown'); }
    else if (oppositionCount >= 1) { s += 0.08; d.indicators.push('Some opposition'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Strong conflict!' : s >= 0.5 ? 'Good conflict.' : s >= 0.3 ? 'Some conflict present.' : 'Develop the conflict more.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreStakes(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // High stakes words
    const stakesWords = ['life', 'death', 'die', 'kill', 'save', 'rescue', 'danger', 'risk', 'everything', 'nothing', 'last chance', 'only hope', 'survival', 'survive', 'lose', 'lost', 'win', 'fail', 'failure', 'success', 'important', 'crucial', 'critical', 'desperate'];
    let stakesCount = 0;
    for (const word of stakesWords) if (lower.includes(word)) stakesCount++;
    if (stakesCount >= 4) { s += 0.35; d.indicators.push('High stakes'); }
    else if (stakesCount >= 2) { s += 0.2; d.indicators.push('Stakes present'); }
    else if (stakesCount >= 1) { s += 0.1; d.indicators.push('Some stakes'); }

    // Consequence words
    const consequenceWords = ['if', 'would', 'could', 'might', 'never', 'always', 'forever', 'everyone', 'no one', 'all', 'nothing'];
    let consequenceCount = 0;
    for (const word of consequenceWords) if (lower.includes(word)) consequenceCount++;
    if (consequenceCount >= 3) { s += 0.15; d.indicators.push('Consequences implied'); }

    // Urgency
    const urgencyWords = ['must', 'have to', 'need to', 'now', 'quickly', 'hurry', 'time', 'before', 'running out'];
    let urgencyCount = 0;
    for (const word of urgencyWords) if (lower.includes(word)) urgencyCount++;
    if (urgencyCount >= 2) { s += 0.15; d.indicators.push('Urgency shown'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'High stakes established!' : s >= 0.5 ? 'Good stakes.' : s >= 0.3 ? 'Some stakes present.' : 'Raise the stakes.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreTurningPoint(text) {
    let s = 0.25;
    const d = { indicators: [] };
    const lower = text.toLowerCase();

    // Turning point signals
    const turningWords = ['suddenly', 'then', 'but then', 'that\'s when', 'in that moment', 'at that instant', 'everything changed', 'realized', 'understood', 'knew', 'decided', 'finally', 'at last'];
    let turningCount = 0;
    for (const word of turningWords) if (lower.includes(word)) turningCount++;
    if (turningCount >= 3) { s += 0.3; d.indicators.push('Clear turning point'); }
    else if (turningCount >= 1) { s += 0.15; d.indicators.push('Turning moment'); }

    // Before/after contrast
    const contrastWords = ['before', 'after', 'used to', 'now', 'no longer', 'never again', 'for the first time', 'unlike before'];
    let contrastCount = 0;
    for (const word of contrastWords) if (lower.includes(word)) contrastCount++;
    if (contrastCount >= 2) { s += 0.2; d.indicators.push('Before/after contrast'); }
    else if (contrastCount >= 1) { s += 0.1; d.indicators.push('Some contrast'); }

    // Decision/action moment
    const decisionWords = ['choice', 'decision', 'chose', 'decided', 'acted', 'did', 'made'];
    let decisionCount = 0;
    for (const word of decisionWords) if (lower.includes(word)) decisionCount++;
    if (decisionCount >= 2) { s += 0.15; d.indicators.push('Decisive moment'); }

    // Peak emotion
    let emotionCount = 0;
    for (const p of NARRATIVE_PATTERNS.emotions) emotionCount += (text.match(p) || []).length;
    if (emotionCount >= 4) { s += 0.15; d.indicators.push('Emotional peak'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent turning point!' : s >= 0.5 ? 'Good climax moment.' : s >= 0.3 ? 'Some turning point.' : 'Show the moment things change.';
    return { rawScore: s, feedback, details: d };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REVISION / EDITING SCORING METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  _scoreErrorCorrection(text, embed) {
    let s = 0.2;
    const d = { indicators: [], relevanceIssue: false };

    // First check: Is the response relevant to the source text?
    // For revision tasks, the revised text should contain similar content/words
    if (this.sourceContent && this.sourceContent.length > 20) {
      const sourceWords = new Set(
        this.sourceContent.toLowerCase().match(/\b[a-z]{3,}\b/g) || []
      );
      const studentWords = text.toLowerCase().match(/\b[a-z]{3,}\b/g) || [];
      
      let overlapCount = 0;
      for (const word of studentWords) {
        if (sourceWords.has(word)) overlapCount++;
      }
      
      const overlapRatio = overlapCount / Math.max(studentWords.length, 1);
      
      if (overlapRatio < 0.15) {
        // Very different content - likely not a revision of the original
        d.relevanceIssue = true;
        d.indicators.push('Response differs significantly from original');
        s = Math.max(0.1, s - 0.5);
      } else if (overlapRatio >= 0.3) {
        s += 0.25;
        d.indicators.push('Maintains original content');
      } else if (overlapRatio >= 0.2) {
        s += 0.15;
        d.indicators.push('Some original content');
      }
    }

    // If clearly off-topic, don't score well
    if (d.relevanceIssue) {
      const feedback = '⚠️ Response does not appear to be a revision of the original. Please fix errors in the provided text.';
      return { rawScore: Math.min(0.2, s), feedback, details: d };
    }

    // Check for common error patterns (absence = good)
    const errorPatterns = [
      { pattern: /\bthier\b/gi, type: 'spelling' },
      { pattern: /\bteh\b/gi, type: 'spelling' },
      { pattern: /\bfrend\b/gi, type: 'spelling' },
      { pattern: /\bbecuase\b/gi, type: 'spelling' },
      { pattern: /\bcould of\b/gi, type: 'grammar' },
      { pattern: /\bshould of\b/gi, type: 'grammar' },
      { pattern: /\bme and my\b/gi, type: 'grammar' },
      { pattern: /\bwe seen\b/gi, type: 'grammar' },
      { pattern: /\brunned\b/gi, type: 'grammar' },
      { pattern: /\bsayed\b/gi, type: 'grammar' },
      { pattern: /\bdidnt\b/gi, type: 'punctuation' },
      { pattern: /\bwasnt\b/gi, type: 'punctuation' },
      { pattern: /\bi\s/g, type: 'capitalization' } // lowercase "i"
    ];

    let errorsFound = 0;
    for (const { pattern, type } of errorPatterns) {
      if (pattern.test(text)) {
        errorsFound++;
        d.indicators.push(`${type} error remains`);
      }
    }

    if (errorsFound === 0) { s += 0.35; d.indicators.push('Errors corrected'); }
    else if (errorsFound <= 2) { s += 0.15; d.indicators.push('Some errors remain'); }
    else { d.indicators.push('Multiple errors remain'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Good error correction!' : s >= 0.5 ? 'Some corrections made.' : s >= 0.3 ? 'More corrections needed.' : 'Please correct the errors in the original text.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreCapitalization(text) {
    let s = 0.5;
    const d = { indicators: [], errors: [] };

    // Check sentence beginnings
    const sentences = this._getSentences(text);
    let correctCaps = 0;
    for (const sent of sentences) {
      if (sent.length > 0 && /^[A-Z]/.test(sent)) {
        correctCaps++;
      } else if (sent.length > 0) {
        d.errors.push('Sentence not capitalized');
      }
    }
    
    if (sentences.length > 0) {
      const capRatio = correctCaps / sentences.length;
      if (capRatio >= 1) { s += 0.3; d.indicators.push('All sentences capitalized'); }
      else if (capRatio >= 0.8) { s += 0.15; d.indicators.push('Most sentences capitalized'); }
      else { s -= 0.2; d.indicators.push('Capitalization issues'); }
    }

    // Check "I" capitalization
    const lowercaseI = (text.match(/\bi\b/g) || []).length;
    if (lowercaseI > 0) {
      s -= Math.min(0.3, lowercaseI * 0.1);
      d.errors.push(`"i" should be "I" (${lowercaseI} times)`);
    } else if (/\bI\b/.test(text)) {
      s += 0.1;
      d.indicators.push('"I" properly capitalized');
    }

    // Check proper nouns (names) - should start with capital
    const properNouns = text.match(/\b[a-z]+(?:\s+[a-z]+)?\b/g) || [];
    // This is a simple heuristic - in reality would need NER

    s = Math.max(0, Math.min(1, s));
    const feedback = s >= 0.8 ? 'Excellent capitalization!' : s >= 0.6 ? 'Good capitalization.' : s >= 0.4 ? 'Some capitalization errors.' : 'Check your capitalization.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSentenceStructure(text) {
    let s = 0.4;
    const d = { indicators: [], issues: [] };

    const sentences = this._getSentences(text);
    
    // Check for complete sentences (subject + verb patterns)
    let completeCount = 0;
    let fragmentCount = 0;
    let runOnCount = 0;

    for (const sent of sentences) {
      const words = sent.split(/\s+/);
      
      // Very short might be fragment
      if (words.length < 3) {
        fragmentCount++;
        d.issues.push('Possible fragment');
      } else {
        completeCount++;
      }

      // Very long might be run-on
      if (words.length > 35) {
        runOnCount++;
        d.issues.push('Possible run-on');
      }
    }

    // Check for run-on indicators (comma splices)
    const commaSplices = (text.match(/,[^"]*\b(he|she|it|they|we|I)\s+(is|are|was|were|went|said|did|had)\b/gi) || []).length;
    if (commaSplices > 0) {
      runOnCount += commaSplices;
      d.issues.push('Possible comma splice');
    }

    if (sentences.length > 0) {
      if (fragmentCount === 0 && runOnCount === 0) {
        s += 0.4;
        d.indicators.push('Complete sentences');
      } else if (fragmentCount + runOnCount <= 1) {
        s += 0.2;
        d.indicators.push('Mostly complete sentences');
      } else {
        s -= 0.2;
        d.indicators.push('Sentence structure issues');
      }
    }

    // Check for proper ending punctuation
    const properEndings = (text.match(/[.!?][""]?\s*$/gm) || []).length;
    if (properEndings >= sentences.length * 0.8) {
      s += 0.1;
      d.indicators.push('Proper punctuation');
    }

    s = Math.max(0, Math.min(1, s));
    const feedback = s >= 0.8 ? 'Excellent sentence structure!' : s >= 0.6 ? 'Good sentence structure.' : s >= 0.4 ? 'Some structure issues.' : 'Check your sentence structure.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreClarity(text) {
    let s = 0.4;
    const d = { indicators: [] };

    // Check for clear, simple sentences
    const sentences = this._getSentences(text);
    const avgLength = sentences.length > 0 
      ? sentences.reduce((sum, s) => sum + s.split(/\s+/).length, 0) / sentences.length 
      : 0;

    // Ideal average sentence length for clarity: 10-20 words
    if (avgLength >= 8 && avgLength <= 22) {
      s += 0.2;
      d.indicators.push('Good sentence length');
    } else if (avgLength > 25) {
      s -= 0.1;
      d.indicators.push('Sentences may be too long');
    } else if (avgLength < 6 && sentences.length > 2) {
      d.indicators.push('Sentences may be too short');
    }

    // Check for unclear pronoun references (many pronouns without clear antecedents)
    const pronouns = (text.match(/\b(it|they|them|this|that|these|those)\b/gi) || []).length;
    const nouns = (text.match(/\b[A-Z][a-z]+\b/g) || []).length;
    
    if (pronouns > 0 && nouns > 0 && pronouns / nouns < 3) {
      s += 0.15;
      d.indicators.push('Clear references');
    } else if (pronouns > nouns * 3) {
      s -= 0.1;
      d.indicators.push('May have unclear references');
    }

    // Check for transition words (help clarity)
    let transitionCount = 0;
    for (const words of Object.values(TRANSITIONS)) {
      for (const w of words) {
        if (text.toLowerCase().includes(w)) transitionCount++;
      }
    }
    if (transitionCount >= 2) {
      s += 0.15;
      d.indicators.push('Uses transitions');
    }

    // Vocabulary level check - simpler words = clearer
    const complexWords = (text.match(/\b[a-zA-Z]{12,}\b/g) || []).length;
    const totalWords = (text.match(/\b[a-zA-Z]+\b/g) || []).length;
    if (totalWords > 0 && complexWords / totalWords < 0.1) {
      s += 0.1;
      d.indicators.push('Clear vocabulary');
    }

    s = Math.max(0, Math.min(1, s));
    const feedback = s >= 0.8 ? 'Very clear writing!' : s >= 0.6 ? 'Clear writing.' : s >= 0.4 ? 'Could be clearer.' : 'Work on clarity.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreSpelling(text) {
    let s = 1.0;
    const d = { errors: [], errorCount: 0 };

    // Common misspellings to check
    const misspellings = [
      { wrong: /\bthier\b/gi, right: 'their' },
      { wrong: /\bteh\b/gi, right: 'the' },
      { wrong: /\brecieve\b/gi, right: 'receive' },
      { wrong: /\bfreind\b/gi, right: 'friend' },
      { wrong: /\bfrend\b/gi, right: 'friend' },
      { wrong: /\balot\b/gi, right: 'a lot' },
      { wrong: /\bdefinately\b/gi, right: 'definitely' },
      { wrong: /\bseperate\b/gi, right: 'separate' },
      { wrong: /\boccured\b/gi, right: 'occurred' },
      { wrong: /\buntill\b/gi, right: 'until' },
      { wrong: /\bwich\b/gi, right: 'which' },
      { wrong: /\bwher\b/gi, right: 'where' },
      { wrong: /\bbecuase\b/gi, right: 'because' },
      { wrong: /\bbigest\b/gi, right: 'biggest' },
      { wrong: /\brunned\b/gi, right: 'ran' },
      { wrong: /\bsayed\b/gi, right: 'said' },
      { wrong: /\bwent too\b/gi, right: 'went to' },
      { wrong: /\bno who\b/gi, right: 'know who' }
    ];

    for (const { wrong, right } of misspellings) {
      const matches = text.match(wrong) || [];
      if (matches.length > 0) {
        s -= matches.length * 0.08;
        d.errors.push({ found: matches[0], correction: right });
        d.errorCount += matches.length;
      }
    }

    s = Math.max(0, s);
    const feedback = d.errorCount === 0
      ? 'No checked spelling patterns were detected.'
      : s >= 0.8
        ? 'A small number of possible spelling errors were detected.'
        : s >= 0.6
          ? 'Review several possible spelling errors.'
          : 'Spelling needs careful review.';
    return { rawScore: s, feedback, details: d };
  }

  _scorePunctuation(text) {
    let s = 0.6;
    const d = { indicators: [], errors: [] };

    // Check for ending punctuation
    const sentences = this._getSentences(text);
    const properEndings = Math.min(
      sentences.length,
      (text.match(/[.!?]["'”’]?(?=\s|$)/g) || []).length
    );
    if (sentences.length > 0) {
      const endingRatio = properEndings / sentences.length;
      if (endingRatio >= 0.9) { s += 0.2; d.indicators.push('Proper ending punctuation'); }
      else if (endingRatio < 0.5) { s -= 0.2; d.errors.push('Missing ending punctuation'); }
    }

    // Check for missing apostrophes in contractions
    const missingApostrophes = (text.match(/\b(didnt|dont|wont|cant|isnt|wasnt|werent|shouldnt|couldnt|wouldnt|hasnt|havent|hadnt|doesnt)\b/gi) || []).length;
    if (missingApostrophes > 0) {
      s -= missingApostrophes * 0.08;
      d.errors.push(`Missing apostrophe (${missingApostrophes} times)`);
    }

    // Check for proper apostrophe use
    const correctContractions = (text.match(/\b(didn't|don't|won't|can't|isn't|wasn't|weren't|shouldn't|couldn't|wouldn't|hasn't|haven't|hadn't|doesn't)\b/gi) || []).length;
    if (correctContractions > 0) {
      s += Math.min(0.15, correctContractions * 0.05);
      d.indicators.push('Proper contractions');
    }

    // Check for comma usage (basic)
    const commas = (text.match(/,/g) || []).length;
    const wordCount = (text.match(/\b\w+\b/g) || []).length;
    if (wordCount > 20 && commas > 0) {
      s += 0.1;
      d.indicators.push('Uses commas');
    }

    // Check for quotation marks (if dialogue present)
    const hasQuotes = /"[^"]+"|"[^"]+"/.test(text);
    if (hasQuotes) {
      s += 0.1;
      d.indicators.push('Proper quotation marks');
    }

    s = Math.max(0, Math.min(1, s));
    const feedback = s >= 0.85 ? 'Excellent punctuation!' : s >= 0.65 ? 'Good punctuation.' : s >= 0.45 ? 'Some punctuation errors.' : 'Check your punctuation.';
    return { rawScore: s, feedback, details: d };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TEXT EVIDENCE / CITATION SCORING METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  _scoreTextEvidence(text, embed) {
    let s = 0.15; // Base score for attempting
    const d = { indicators: [], quotesFound: 0, paraphrases: 0, references: 0 };

    // 1. Direct quotes from passage
    const quotes = text.match(/"[^"]{5,}"|"[^"]{5,}"|'[^']{10,}'/g) || [];
    d.quotesFound = quotes.length;
    if (quotes.length >= 2) { s += 0.25; d.indicators.push('Multiple quotes'); }
    else if (quotes.length >= 1) { s += 0.2; d.indicators.push('Quote present'); }

    // 2. Citation phrases
    const citationPhrases = [
      /\b(the text states|the passage says|according to the text|the author writes|as stated|as mentioned)\b/i,
      /\b(in the passage|in the text|the story says|the article states|we learn that)\b/i,
      /\b(the passage shows|this shows that|this demonstrates|this proves|evidence of this)\b/i,
      /\b(for example|for instance|one example|another example)\b/i,
      /\b(paragraph \d|line \d|on page|in line)\b/i
    ];
    
    let citationCount = 0;
    for (const p of citationPhrases) {
      if (p.test(text)) citationCount++;
    }
    d.references = citationCount;
    if (citationCount >= 3) { s += 0.25; d.indicators.push('Strong text references'); }
    else if (citationCount >= 2) { s += 0.2; d.indicators.push('Good text references'); }
    else if (citationCount >= 1) { s += 0.15; d.indicators.push('References text'); }

    // 3. Paraphrasing indicators
    const paraphrasePhrases = [
      /\b(in other words|this means that|basically|essentially|the author means)\b/i,
      /\b(this suggests|this implies|this indicates|we can infer)\b/i
    ];
    for (const p of paraphrasePhrases) {
      if (p.test(text)) { d.paraphrases++; }
    }
    if (d.paraphrases >= 2) { s += 0.15; d.indicators.push('Paraphrases evidence'); }
    else if (d.paraphrases >= 1) { s += 0.1; d.indicators.push('Some paraphrasing'); }

    // 4. Neural similarity to source (actual content from passage)
    if (embed && this.passageEmbeddings.length > 0) {
      let maxSim = 0;
      for (const e of this.passageEmbeddings) {
        const sim = cosineSimilarity(embed, e);
        if (sim > maxSim) maxSim = sim;
      }
      if (maxSim > 0.6) { s += 0.15; d.indicators.push('Content matches passage'); }
      else if (maxSim > 0.4) { s += 0.1; d.indicators.push('Related to passage'); }
    }

    // 5. Keyword overlap with source
    if (this.sourceKeywords.size > 0) {
      const textWords = new Set(text.toLowerCase().split(/\s+/));
      let overlap = 0;
      for (const kw of this.sourceKeywords) if (textWords.has(kw)) overlap++;
      const overlapRatio = overlap / this.sourceKeywords.size;
      if (overlapRatio > 0.3) { s += 0.1; d.indicators.push('Uses passage vocabulary'); }
    }

    s = Math.min(1, s);
    const feedback = s >= 0.75 ? 'Excellent use of text evidence!' : 
                     s >= 0.55 ? 'Good text evidence.' : 
                     s >= 0.35 ? 'Some evidence from text.' : 
                     'Cite more evidence from the passage.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreQuotes(text) {
    let s = 0.2;
    const d = { indicators: [], quotes: [] };

    // Find all quoted text
    const doubleQuotes = text.match(/"([^"]{5,})"/g) || [];
    const curlyQuotes = text.match(/"([^"]{5,})"/g) || [];
    const allQuotes = [...doubleQuotes, ...curlyQuotes];
    d.quotes = allQuotes;

    if (allQuotes.length >= 3) { s += 0.5; d.indicators.push('Multiple quotes used'); }
    else if (allQuotes.length >= 2) { s += 0.4; d.indicators.push('Two quotes'); }
    else if (allQuotes.length >= 1) { s += 0.3; d.indicators.push('Quote present'); }

    // Check for quote attribution
    const attributions = ['said', 'states', 'writes', 'explains', 'describes', 'mentions', 'according to'];
    let hasAttribution = false;
    const lower = text.toLowerCase();
    for (const attr of attributions) {
      if (lower.includes(attr)) { hasAttribution = true; break; }
    }
    if (hasAttribution && allQuotes.length > 0) { s += 0.1; d.indicators.push('Quotes attributed'); }

    // Check if quotes are integrated (not just dropped in)
    if (allQuotes.length > 0) {
      const beforeQuote = /\b(that|when|because|since|as)\s*[""]/.test(text);
      const afterQuote = /[""]\s*(which|this|shows|proves|demonstrates)/.test(text);
      if (beforeQuote || afterQuote) { s += 0.1; d.indicators.push('Quotes integrated'); }
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent use of quotes!' : 
                     s >= 0.5 ? 'Good quoting.' : 
                     s >= 0.3 ? 'Quote present.' : 
                     'Add direct quotes from the text.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreParaphrasing(text, embed) {
    let s = 0.2;
    const d = { indicators: [] };

    // Paraphrase signal words
    const signals = [
      /\b(in other words|this means|basically|essentially)\b/i,
      /\b(the author is saying|what this means is|put simply)\b/i,
      /\b(this suggests that|this shows that|this indicates)\b/i,
      /\b(we can see that|it is clear that|this demonstrates)\b/i
    ];
    
    let signalCount = 0;
    for (const p of signals) if (p.test(text)) signalCount++;
    if (signalCount >= 2) { s += 0.3; d.indicators.push('Clear paraphrasing'); }
    else if (signalCount >= 1) { s += 0.2; d.indicators.push('Paraphrase signals'); }

    // Neural similarity (paraphrase should relate to source but not be identical)
    if (embed && this.passageEmbeddings.length > 0) {
      let maxSim = 0;
      for (const e of this.passageEmbeddings) {
        const sim = cosineSimilarity(embed, e);
        if (sim > maxSim) maxSim = sim;
      }
      // Ideal paraphrase: similar meaning (0.4-0.7) but not copied (>0.85)
      if (maxSim >= 0.4 && maxSim <= 0.85) { 
        s += 0.25; 
        d.indicators.push('Good paraphrase of source'); 
      } else if (maxSim > 0.85) {
        d.indicators.push('May be too close to original');
      }
    }

    // Explanation/interpretation words
    const interpretation = /\b(meaning|implies|suggests|shows|reveals|demonstrates|proves)\b/i;
    if (interpretation.test(text)) { s += 0.15; d.indicators.push('Interprets text'); }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent paraphrasing!' : 
                     s >= 0.5 ? 'Good paraphrasing.' : 
                     s >= 0.3 ? 'Some paraphrasing.' : 
                     'Paraphrase evidence in your own words.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreTextReference(text, embed) {
    let s = 0.2;
    const d = { indicators: [], referenceTypes: [] };

    // Direct text reference phrases
    const directRefs = [
      { pattern: /\b(the text states|the passage says|the author writes)\b/i, type: 'direct' },
      { pattern: /\b(according to the text|according to the passage|according to the author)\b/i, type: 'attribution' },
      { pattern: /\b(in the passage|in the text|in the story|in the article)\b/i, type: 'location' },
      { pattern: /\b(paragraph \d|line \d|page \d|section \d)\b/i, type: 'specific' },
      { pattern: /\b(the passage shows|the text demonstrates|the author explains)\b/i, type: 'interpretive' },
      { pattern: /\b(we learn that|we see that|we find that|it says that)\b/i, type: 'general' }
    ];

    for (const { pattern, type } of directRefs) {
      if (pattern.test(text)) {
        d.referenceTypes.push(type);
        s += 0.15;
      }
    }

    if (d.referenceTypes.length >= 3) { d.indicators.push('Multiple reference types'); }
    else if (d.referenceTypes.length >= 2) { d.indicators.push('Good text references'); }
    else if (d.referenceTypes.length >= 1) { d.indicators.push('References passage'); }

    // Bonus for specific references (paragraph/line numbers)
    if (d.referenceTypes.includes('specific')) { s += 0.1; d.indicators.push('Specific citation'); }

    // Neural connection to passage
    if (embed && this.passageEmbeddings.length > 0) {
      let maxSim = 0;
      for (const e of this.passageEmbeddings) {
        const sim = cosineSimilarity(embed, e);
        if (sim > maxSim) maxSim = sim;
      }
      if (maxSim > 0.5) { s += 0.15; d.indicators.push('Content from passage'); }
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Excellent text references!' : 
                     s >= 0.5 ? 'Good references to text.' : 
                     s >= 0.3 ? 'Some text reference.' : 
                     'Reference the passage more directly.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreEvidenceIntegration(text) {
    let s = 0.25;
    const d = { indicators: [] };

    // Check for quotes
    const quotes = (text.match(/"[^"]+"|"[^"]+"/g) || []).length;
    if (quotes >= 1) { s += 0.15; d.indicators.push('Includes quotes'); }

    // Check for lead-in to quotes
    const leadIns = [
      /\b(states that|explains that|writes that|says that)\s*[""]/.test(text),
      /\b(according to|as .+ (writes|states|explains))\s*,?\s*[""]/.test(text),
      /\b(for example|for instance),?\s*[""]/.test(text)
    ];
    if (leadIns.some(x => x)) { s += 0.2; d.indicators.push('Quote introduced'); }

    // Check for explanation after evidence
    const followUp = [
      /[""]\s*(This (shows|proves|demonstrates|means|suggests))/.test(text),
      /[""]\s*\([^)]+\)\s*\.?\s*(This|The)/.test(text),
      /[.!?]\s*(This (quote|evidence|example)|In other words)/.test(text)
    ];
    if (followUp.some(x => x)) { s += 0.2; d.indicators.push('Evidence explained'); }

    // Check for analysis/interpretation
    const analysis = /\b(this (shows|proves|demonstrates|suggests|means|implies)|therefore|thus|as a result)\b/i;
    if (analysis.test(text)) { s += 0.15; d.indicators.push('Analyzes evidence'); }

    // Overall flow: intro + evidence + analysis pattern
    const sentences = this._getSentences(text);
    if (sentences.length >= 3 && quotes >= 1) { 
      s += 0.1; 
      d.indicators.push('Evidence in context'); 
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? 'Evidence well integrated!' : 
                     s >= 0.5 ? 'Good evidence integration.' : 
                     s >= 0.3 ? 'Evidence present.' : 
                     'Integrate evidence more smoothly.';
    return { rawScore: s, feedback, details: d };
  }

  _scoreGeneric(text, embed, criterion) {
    let s = 0.5;
    const d = { matchedConcepts: [] };

    if (embed && this.passageEmbeddings.length > 0) {
      let max = 0;
      for (const e of this.passageEmbeddings) {
        const sim = cosineSimilarity(embed, e);
        if (sim > max) max = sim;
      }
      s = 0.3 + max * 0.5;
    }

    const words = criterion.name.toLowerCase().split(/\s+/);
    for (const w of words) {
      if (text.toLowerCase().includes(w)) {
        s += 0.1;
        d.matchedConcepts.push(w);
      }
    }

    s = Math.min(1, s);
    const feedback = s >= 0.7 ? `Good ${criterion.name}.` : s >= 0.4 ? `Addresses "${criterion.name}".` : `Needs work on "${criterion.name}".`;
    return { rawScore: s, feedback, details: d };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FEEDBACK GENERATION
  // ═══════════════════════════════════════════════════════════════════════════

  _genFeedback(scores, criteria, analysis, pct) {
    const fb = { overall: '', strengths: [], improvements: [], suggestions: [] };

    const sorted = criteria.map(c => ({ name: c.name, score: scores[c.name]?.rawScore || 0 })).sort((a, b) => b.score - a.score);
    fb.strengths = sorted.filter(c => c.score >= 0.7).slice(0, 3).map(c => c.name);
    fb.improvements = sorted.filter(c => c.score < 0.5).slice(-3).map(c => c.name);

    let txt = '';
    
    // Repetition warning
    if (analysis.repetitionPenalty > 0.4) {
      txt += '⚠️ Significant repetition detected. Avoid copying content multiple times. ';
    } else if (analysis.repetitionPenalty > 0.2) {
      txt += 'Note: Some repetitive content detected. ';
    }

    // Overall assessment
    if (pct >= 85) txt += 'Excellent work! Your writing demonstrates strong skills. ';
    else if (pct >= 70) txt += 'Good work! Your writing shows solid understanding. ';
    else if (pct >= 55) txt += 'Satisfactory work with room for improvement. ';
    else txt += 'This response needs revision. ';

    // Strengths and improvements
    if (fb.strengths.length > 0) txt += `Strengths: ${fb.strengths.join(', ')}. `;
    if (fb.improvements.length > 0) txt += `Work on: ${fb.improvements.join(', ')}.`;

    fb.overall = txt.trim();

    // Suggestions
    if (analysis.vocabulary?.suggestions) fb.suggestions.push(...analysis.vocabulary.suggestions);
    if (analysis.sentenceVariety?.variety === 'monotonous') fb.suggestions.push('Vary your sentence lengths and beginnings.');
    if (!analysis.argument?.hasEvidence) fb.suggestions.push('Add specific evidence or examples from the text.');
    if (!analysis.argument?.hasConclusion) fb.suggestions.push('Add a clear conclusion to wrap up your ideas.');
    if (analysis.tone?.confidence === 'tentative') fb.suggestions.push('Use more confident language to strengthen your argument.');

    fb.suggestions = fb.suggestions.slice(0, 4);
    return fb;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  _getSentences(text) {
    if (!text) return [];
    return text
      .replace(/Mr\./g, 'Mr\u0000')
      .replace(/Mrs\./g, 'Mrs\u0000')
      .replace(/Dr\./g, 'Dr\u0000')
      .replace(/vs\./g, 'vs\u0000')
      .replace(/etc\./g, 'etc\u0000')
      .replace(/e\.g\./g, 'eg\u0000')
      .replace(/i\.e\./g, 'ie\u0000')
      .split(/[.!?]+/)
      .map(s => s.replace(/\u0000/g, '.').trim())
      .filter(s => s.length > 5);
  }

  _isStopWord(w) {
    const stops = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by', 'from', 'and', 'but', 'or', 'not', 'this', 'that', 'it', 'they', 'he', 'she', 'we', 'you', 'i']);
    return stops.has(w.toLowerCase());
  }

  _calcWeighted(scores, criteria) {
    let t = 0, m = 0;
    for (const c of criteria) {
      const s = scores[c.name];
      if (s) { t += s.points; m += c.weight; }
    }
    return { total: Math.round(t * 10) / 10, maxPossible: m };
  }

  _calcEvidence(text, embed) {
    let s = 0;
    if (this.sourceKeywords.size > 0) {
      const tw = new Set(text.toLowerCase().split(/\s+/));
      const overlap = [...this.sourceKeywords].filter(k => tw.has(k)).length;
      s += overlap / this.sourceKeywords.size * 0.5;
    }
    if (embed && this.passageEmbeddings.length > 0) {
      let max = 0;
      for (const e of this.passageEmbeddings) {
        const sim = cosineSimilarity(embed, e);
        if (sim > max) max = sim;
      }
      s += max * 0.5;
    }
    return Math.round(s * 100) / 100;
  }

  _emptyResult(criteria) {
    const scores = {};
    for (const c of criteria) {
      scores[c.name] = { rawScore: 0, points: 0, maxPoints: c.weight, feedback: 'No response provided.', details: {} };
    }
    const emptyAnalysis = {
      wordCount: 0,
      sentenceCount: 0,
      paragraphCount: 0,
      repetitionPenalty: 0,
      coherence: 0,
      sentenceVariety: { score: 0, variety: 'insufficient', avgLength: 0 },
      argument: {}
    };
    return {
      scores,
      total: 0,
      maxPossible: criteria.reduce((s, c) => s + c.weight, 0),
      percentage: 0,
      evidenceScore: 0,
      overallFeedback: 'Please provide a response.',
      wordCount: 0,
      sentenceCount: 0,
      analysisMode: this.trainingMode,
      writingProfiles: this._buildWritingProfiles({
        text: '',
        embed: null,
        analysis: emptyAnalysis,
        rubricCriteria: criteria,
        holisticPercentage: 0
      })
    };
  }

  _heuristicScore(text, criteria) {
    if (!text || text.trim().length < 10) return this._emptyResult(criteria);
    
    const a = this._comprehensiveAnalysis(text);
    const scores = {};
    for (const c of criteria) {
      scores[c.name] = this._scoreCriterion(text, null, c, a);
    }
    
    const { total, maxPossible } = this._calcWeighted(scores, criteria);
    const pct = maxPossible > 0 ? Math.round((total / maxPossible) * 100) : 0;
    const relevancePenalty = this._detectOffTopic(text, null);
    const entityPenalty = this._checkEntityMatching(text).penalty;
    
    return {
      scores,
      total,
      maxPossible,
      percentage: pct,
      evidenceScore: this._calcEvidence(text, null),
      overallFeedback: this._genFeedback(scores, criteria, a, pct).overall,
      wordCount: a.wordCount,
      sentenceCount: a.sentenceCount,
      analysisMode: 'heuristic',
      writingProfiles: this._buildWritingProfiles({
        text,
        embed: null,
        analysis: a,
        rubricCriteria: criteria,
        holisticPercentage: pct,
        repetitionPenalty: a.repetitionPenalty,
        relevancePenalty,
        entityPenalty
      })
    };
  }

  // Public API
  getStats() { return { ...this.stats }; }
  
  reset() {
    this.trained = false;
    this.trainingMode = 'heuristic';
    this.passageChunks = [];
    this.passageEmbeddings = [];
    this.sourceContent = '';
    this.sourceKeywords = new Set();
    this.sourceSentences = [];
    
    // Reset question analysis
    this.questionText = '';
    this.questionType = 'general';
    this.questionRequirements = [];
    this.questionKeywords = new Set();
    this.questionEntities = [];
    
    // Reset passage entities
    this.passageEntities = {
      names: [],
      places: [],
      objects: [],
      concepts: [],
      requiredTerms: []
    };
  }
  
  isReady() { return this.trained; }
  
  getTrainingMode() { return this.trainingMode; }
  
  getQuestionType() { return this.questionType; }
  
  getQuestionRequirements() { return this.questionRequirements; }
  
  getPassageEntities() { return this.passageEntities; }
}
